import React, { useState, useMemo, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getExpandedRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  Globe,
  TrendingUp,
  MousePointer,
  Flag,
  Mail,
  ChevronsRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Search,
  X,
  ChevronDown,
  ChevronRight as ChevronRightIcon,
  Settings,
  Eye,
  EyeOff,
  Settings2Icon,
  Settings2,
  BookmarkPlus,
  Plus,
  DraftingCompass,
  // Bookmark, // Commented out since it's not being used
  BarChart3,
  Loader2,
  Crown,
  Tag,
  UserSearch,
  Linkedin,
  Twitter,
  Facebook,
  Instagram,
  Share2,
  ExternalLink,
  Target,
  Trash2,
  ShieldCheck,
  UserCheck,
  Check
} from 'lucide-react';
import { countryList } from './countryList';
import { CountryFlag } from './countryFlagService';
import { resolvePaidChannels, channelMeta, describeChannels } from './paidChannels';
import PaidAdvertisingPanel from './PaidAdvertisingPanel';
import { useTooltip, Tooltip } from './toolTip';
import EmailPopup from '../component/emailpop/EmailPopup';
import ContactPopup from '../component/contacts/ContactPopup';
import { isRowVisible, useRowLimitOverride } from './planRowLimit';

// --- Forex-vertical Deal / entity-type columns -----------------------------
// Populated only for the `Trading & Investment` vertical (migrations/
// add_forex_deal_columns.sql, controllers/affiliateRecruitment/fetchData.js).
// Every gambling row has entity_type/deal_types/deal_details/platforms = null,
// so the Deal column and this badge simply never render for them — see the
// `showDealColumn` gate below, which only turns the column on when at least
// one row on the CURRENT PAGE actually carries deal_types.
const ENTITY_TYPE_META = {
  website: { label: 'Website', className: 'bg-slate-100 text-slate-700 dark:bg-slate-700/60 dark:text-slate-200' },
  influencer: { label: 'Influencer', className: 'bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300' },
  streamer: { label: 'Streamer', className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' },
  signal_provider: { label: 'Signal Provider', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  ea_vendor: { label: 'EA Vendor', className: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300' },
  community: { label: 'Community', className: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300' },
  education: { label: 'Education', className: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300' },
};

// --- Regulator column -------------------------------------------------------
// Filled by the mars-affiliate-licence-checker crawler, which reads an
// affiliate's site, works out which casino and betting brands it promotes, and
// records the licence authorities behind them
// (affiliate_url_licence.authorities → `licence_authorities` on the row).
//
// The crawler is still working through affiliate_clean, so most rows have
// nothing here yet — see the `showRegulatorColumn` gate below, which only turns
// the column on when at least one row on the CURRENT PAGE carries authorities.
//
// The verdict over those authorities, worded to match the Licence filter's
// two options ('licensed' / 'offshore'): a row the filter returns for BOTH
// picks says so. 'mixed' is the one worth reading carefully — the affiliate
// promotes nationally licensed operators AND offshore ones, which is a
// different proposition to either.
const LICENCE_STATUS_META = {
  regulated:   { label: 'Licensed',            className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
  mixed:       { label: 'Licensed + Offshore', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  unregulated: { label: 'Offshore',            className: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300' },
  unknown:     { label: 'Unknown',             className: 'bg-slate-100 text-slate-600 dark:bg-slate-700/60 dark:text-slate-300' },
};

// Deal-details popover for the Deal column. `advertised` numbers come from the
// brand's own terms page; `inferred` ones are a model's best guess from
// context and are deliberately NEVER shown as numbers here — only the
// qualitative bits (brokers listed, notes, evidence link) are, so nobody
// mistakes a guess for a published rate.
const DealDetailsPopup = ({ isOpen, onClose, row }) => {
  if (!isOpen || !row) return null;

  const details = row.deal_details || {};
  const isAdvertised = details.stance_source === 'advertised';
  const dealTypes = String(row.deal_types || '').split(',').map((d) => d.trim()).filter(Boolean);
  const entityMeta = ENTITY_TYPE_META[row.entity_type] || null;
  let platforms = row.platforms;
  if (typeof platforms === 'string') {
    try { platforms = JSON.parse(platforms); } catch (_) { platforms = null; }
  }

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-md w-full p-5 max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-slate-100">Deal details</h3>
            {entityMeta && (
              <span className={`inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-medium ${entityMeta.className}`}>
                {entityMeta.label}
              </span>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-200">
            <X className="w-4 h-4" />
          </button>
        </div>

        {dealTypes.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {dealTypes.map((dt, idx) => (
              <span key={idx} className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">
                {dt}
              </span>
            ))}
          </div>
        )}

        <div className="mb-3">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold ${
            isAdvertised
              ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
              : 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-300'
          }`}>
            {isAdvertised ? 'Advertised by the brand' : 'Inferred — not published by the brand'}
          </span>
        </div>

        {isAdvertised ? (
          <dl className="space-y-1.5 text-xs text-gray-700 dark:text-slate-300">
            {details.cpa_range && (
              <div className="flex justify-between"><dt className="text-gray-500 dark:text-slate-400">CPA</dt><dd className="font-medium">{details.cpa_range}</dd></div>
            )}
            {details.revshare_pct && (
              <div className="flex justify-between"><dt className="text-gray-500 dark:text-slate-400">RevShare</dt><dd className="font-medium">{details.revshare_pct}</dd></div>
            )}
            {details.ib_rebate && (
              <div className="flex justify-between"><dt className="text-gray-500 dark:text-slate-400">IB Rebate</dt><dd className="font-medium">{details.ib_rebate}</dd></div>
            )}
            {details.banner_placement && (
              <div className="flex justify-between"><dt className="text-gray-500 dark:text-slate-400">Banner placement</dt><dd className="font-medium">Yes</dd></div>
            )}
            {details.ranking_position_sold && (
              <div className="flex justify-between"><dt className="text-gray-500 dark:text-slate-400">Ranking position sold</dt><dd className="font-medium">Yes</dd></div>
            )}
            {details.marketing_budget_required && (
              <div className="flex justify-between"><dt className="text-gray-500 dark:text-slate-400">Marketing budget required</dt><dd className="font-medium">Yes</dd></div>
            )}
          </dl>
        ) : (
          <p className="text-xs text-gray-500 dark:text-slate-400 italic">
            No brand-published numbers — this deal shape was inferred from context, not advertised, so none are shown.
          </p>
        )}

        {Array.isArray(details.brokers_listed) && details.brokers_listed.length > 0 && (
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-slate-500 mb-1">Brokers listed</div>
            <div className="flex flex-wrap gap-1">
              {details.brokers_listed.map((b, idx) => (
                <span key={idx} className="px-1.5 py-0.5 rounded text-[10px] bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300">{b}</span>
              ))}
            </div>
          </div>
        )}

        {Array.isArray(platforms) && platforms.length > 0 && (
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-slate-500 mb-1">Platforms</div>
            <div className="space-y-1">
              {platforms.map((p, idx) => (
                <div key={idx} className="flex items-center justify-between text-xs text-gray-600 dark:text-slate-300">
                  <span className="capitalize">{p.platform}{p.handle ? ` — ${p.handle}` : ''}</span>
                  <div className="flex items-center gap-2">
                    {p.followers != null && (
                      <span className="text-gray-400 dark:text-slate-500">{Number(p.followers).toLocaleString()} followers</span>
                    )}
                    {p.url && (
                      <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {details.notes && (
          <p className="mt-3 text-xs text-gray-600 dark:text-slate-300 whitespace-pre-wrap">{details.notes}</p>
        )}

        {details.evidence_url && (
          <a href={details.evidence_url} target="_blank" rel="noopener noreferrer"
             className="mt-3 inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline">
            <ExternalLink className="w-3 h-3" /> View evidence
          </a>
        )}
      </div>
    </div>
  );
};

const UniversalTable = ({
  data = [],
  paginationInfo = null,
  onPageChange = () => {},
  onFetchData = null, // Specific prop for data fetching
  onPageSizeChange = null, // NEW: Handler for page size changes
  onSearch = null, // NEW: Handler for server-side search
  // Extra controls for the toolbar, rendered before the search box — a page's
  // own filters (the Live Affiliate country picker) sit with the search and
  // page-size controls they act alongside. Should be ~38px tall to line up.
  toolbarControls = null,
  title = "Data Table",
  searchable = true,
  defaultPageSize = 10,
  expandable = true,
  customColumns = null, // Allow custom column definitions
  renderExpandedRow = null, // Custom render function for expanded content
  loading = false,
  loadingMessage = null, // Custom loading message (e.g. "Checking relevancy...")
  userPlan,
  rowLimit = undefined, // per-account row-visibility override; falls back to the shared fetch
  onUpgrade = () => {}, // Add handler for upgrade clicks
  sortByLatest = false, // Toggle state for latest affiliate sorting
  onSortByLatestChange = null, // Callback when toggle changes
  singleExpand = false, // When true, only one row can be expanded at a time
  hidePageSize = false, // When true, hide the page size selector
  renderBeforeRow = null, // Render custom content before a row (e.g. separator strip)
  onDelete = null, // Handler for deleting a prospect — receives row data
  // Affiliate-recruitment-specific: when set, the row expand renders a per-vertical
  // "Relevant Pages" section sourced from row.original.scanned_pages_json. Keys
  // in scanned_pages_json look like "GB:casino" / "GB:sports" — populated by the
  // untapped opportunity validation pipeline. When unset, the existing generic
  // "SERP Validated Pages" section renders as before (other tables unchanged).
  selectedVerticals = null,
  selectedCountries = null,
  // ISO 2-letter codes for selectedCountries (e.g. ["GB"] when selectedCountries = ["United Kingdom"]).
  // Used for scanned_pages_json key lookup since saveScannedPages stores keys by ISO code, not full name.
  // Falls back to selectedCountries if not provided.
  selectedCountryCodes = null,
  // The vertical of the brand this table is showing, when the page has one
  // ('Trading & Investment', 'Casino,Sports Betting'). affiliate_clean.vertical
  // is comma-joined and pipelines APPEND to it, so a site already known as a
  // casino affiliate that later starts listing a broker ends up
  // 'Casino,Crypto,Sports Betting,Trading & Investment'. On a brand's own page
  // the other verticals are noise, so the Vertical column shows only the ones
  // the brand shares. Unset (recruitment browse, paid traffic) = show them all.
  brandVertical = null,
  // "Already approached": a button in the Action column, next to the +, opens the
  // account's brands, and approached rows are highlighted in amber; picking one marks the site approached for it (or takes the mark
  // back). The page's "Already Approached" filter then hides (No) or shows only
  // (Yes) those sites. Affiliate Recruitment, Affiliate Directory and Untapped
  // Opportunities turn it on. onApproachedChange(affiliateUrl) lets a page drop
  // any results it cached.
  approachable = false,
  onApproachedChange = null
}) => {
  // Per-account row-visibility override. A caller that already holds the
  // value can pass it as a prop; otherwise this reads the shared cache, which
  // fetches /api/planfetch once per page load for the whole app.
  const fetchedRowLimit = useRowLimitOverride();
  const effectiveRowLimit = rowLimit === undefined ? fetchedRowLimit : rowLimit;

  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [columnVisibility, setColumnVisibility] = useState({});
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInputValue, setSearchInputValue] = useState(''); // For controlled input
  // FIXED: Initialize with defaultPageSize or from paginationInfo
  const [rowsPerPage, setRowsPerPage] = useState(paginationInfo?.pageSize || defaultPageSize);
  const [emailPopupVisible, setEmailPopupVisible] = useState(false);
  const [selectedEmailData, setSelectedEmailData] = useState('');
  const [selectedSocialMedia, setSelectedSocialMedia] = useState({});
  const [findingContact, setFindingContact] = useState({}); // Track loading state per row
  const [contactResults, setContactResults] = useState({}); // Store found contacts per affiliate
  const [socialMediaResults, setSocialMediaResults] = useState({}); // Store social media per affiliate
  const [pipelineStatuses, setPipelineStatuses] = useState({}); // Track CRM pipeline status per affiliate URL
  const [addingToPipeline, setAddingToPipeline] = useState({}); // Loading state for adding to pipeline
  const [userBrands, setUserBrands] = useState([]); // User's brands for pipeline brand selection
  const [brandPopup, setBrandPopup] = useState({ visible: false, affiliateUrl: '', email: '', prospectId: null, prospectBrandId: null }); // Brand selection popup state
  // Pre-computed contacts from affiliate_contacts, keyed by row id. Holds every
  // channel we stored for a site, not just email -- see contactPopup below.
  const [storedContacts, setStoredContacts] = useState({});
  const [contactPopup, setContactPopup] = useState({ visible: false, domain: '', contacts: [], scan: null, authors: [] });
  // A "Find Contact" lookup that turned up the site's authors, keyed by row id:
  // { contacts, authors } in ContactPopup's shape, so the row can reopen it.
  const [lookupResults, setLookupResults] = useState({});
  // Lookups that passed their deadline and are finishing on the server.
  const [pendingLookups, setPendingLookups] = useState({});
  // Deal-details popover for the Forex `Deal` column — see DealDetailsPopup above.
  const [dealPopup, setDealPopup] = useState({ visible: false, row: null });
  const [confirmingRemove, setConfirmingRemove] = useState(null); // Pipeline entry id being confirmed for removal
  // Approached marks per affiliate URL: { brands: [{ brand_id, brand_name }], emailed }.
  const [approachedStatus, setApproachedStatus] = useState({});
  const [approachPopup, setApproachPopup] = useState({ visible: false, affiliateUrl: '' });
  const [savingApproach, setSavingApproach] = useState(null); // brand id being saved in the popup
  const [approachError, setApproachError] = useState('');

  // Debounce timer ref for server-side search
  const searchDebounceRef = React.useRef(null);

  // API BASE PATH
  const basePath = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

  // DEBUG: Log onSearch prop on mount and when it changes
  useEffect(() => {
  }, [onSearch, title, searchable, loading]);

  // Handle search input change with debouncing for server-side search
  const handleSearchInputChange = (value) => {
    setSearchInputValue(value);

    if (onSearch) {
      // Server-side search with debouncing
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      searchDebounceRef.current = setTimeout(() => {
        onSearch(value);
      }, 500); // 500ms debounce
    } else {
      // Client-side filtering (immediate)
      setGlobalFilter(value);
    }
  };

  // A lookup's result in ContactPopup's shape: its addresses (with the name and
  // role of the author who owns one) and the site's own profiles.
  const lookupToPopupContacts = (result) => [
    ...(result.contacts || []).filter(c => c && c.value).map(c => ({
      channel: 'email',
      value: c.value,
      name: [c.firstName, c.lastName].filter(Boolean).join(' ') || undefined,
      jobTitle: c.position || undefined,
      role: c.source === 'pattern_guess' ? 'guess' : c.source === 'author_page' ? 'personal' : undefined
    })),
    ...Object.entries(result.socialMedia || {}).flatMap(([platform, urls]) =>
      (urls || []).filter(Boolean).map(value => ({ channel: 'social', platform, value })))
  ];

  // Handler for contact finder with fallback
  const handleFindContact = async (affiliateUrl, rowId) => {
    setFindingContact(prev => ({ ...prev, [rowId]: true }));
    setPendingLookups(prev => ({ ...prev, [rowId]: false }));

    try {
      const response = await fetch(`${basePath}/api/affiliate-recruitment/find-hunter-contact`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ affiliateUrl })
      });

      // The proxy gave up before the website search answered. The search
      // carries on and is kept for an hour, so a retry gets its result.
      if (response.status === 502 || response.status === 504) {
        setPendingLookups(prev => ({ ...prev, [rowId]: true }));
        return;
      }

      const result = await response.json();

      if (result.success) {
        const authors = Array.isArray(result.authors) ? result.authors : [];
        const hasSocial = result.socialMedia && Object.values(result.socialMedia).some(arr => arr && arr.length > 0);

        // Store contacts
        if (result.contacts && result.contacts.length > 0) {
          const emails = result.contacts.map(c => c.value).filter(Boolean).join(', ');
          setContactResults(prev => ({ ...prev, [rowId]: emails }));
          // The composer has nowhere to show authors, so a lookup that found
          // some opens the contact list below instead.
          if (authors.length === 0) {
            setSelectedEmailData(emails);
            setSelectedSocialMedia(result.socialMedia || {});
            setEmailPopupVisible(true);
          }
        }

        if (authors.length > 0) {
          const found = { contacts: lookupToPopupContacts(result), authors };
          setLookupResults(prev => ({ ...prev, [rowId]: found }));
          setContactPopup({ visible: true, domain: affiliateUrl, scan: null, ...found });
        }

        // Store social media
        if (hasSocial) {
          setSocialMediaResults(prev => ({ ...prev, [rowId]: result.socialMedia }));
        }

        const nothingFound = (!result.contacts || result.contacts.length === 0) && !hasSocial && authors.length === 0;
        if (nothingFound && result.pending) {
          // Hunter had nothing and the website search ran out of time. It
          // finishes on the server, so the button stays live for a retry.
          setPendingLookups(prev => ({ ...prev, [rowId]: true }));
        } else if (nothingFound) {
          // If no contacts or social media found - just mark as no contacts (no alert)
          setContactResults(prev => ({ ...prev, [rowId]: 'NO_CONTACTS' }));
        }
      } else {
        setContactResults(prev => ({ ...prev, [rowId]: 'NO_CONTACTS' }));
      }
    } catch (error) {
      console.error('Error finding contacts:', error);
      setContactResults(prev => ({ ...prev, [rowId]: 'ERROR' }));
    } finally {
      setFindingContact(prev => ({ ...prev, [rowId]: false }));
    }
  };

  // Create column helper
  const columnHelper = createColumnHelper();
  const { tooltipData, tooltipPosition, showTooltip, hideTooltip, forceHideTooltip } = useTooltip();

  // Hide tooltip on scroll or click outside
  useEffect(() => {
    const handleScroll = () => {
      if (tooltipData) {
        forceHideTooltip();
      }
    };

    const handleClick = (e) => {
      // Hide tooltip when clicking anywhere
      if (tooltipData) {
        forceHideTooltip();
      }
    };

    window.addEventListener('scroll', handleScroll, true);
    document.addEventListener('click', handleClick);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('click', handleClick);
    };
  }, [tooltipData, forceHideTooltip]);

  // URL formatting helper function
  const formatUrl = (url) => {
    if (!url) return '#';
    
    // If it already has a protocol, return as is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    // Add https:// if missing
    return `https://${url}`;
  };

  // Helper function to remove duplicates and normalize keywords
  const getUniqueKeywords = (keywordString) => {
    if (!keywordString) return [];
    
    const keywords = keywordString.split(',')
      .map(keyword => keyword.trim().toLowerCase()) // Normalize to lowercase for comparison
      .filter(keyword => keyword.length > 0); // Remove empty keywords
    
    // Use Set to remove duplicates, then convert back to array
    const uniqueKeywords = [...new Set(keywords)];
    
    // Return original case keywords by finding first occurrence
    return uniqueKeywords.map(uniqueKeyword => {
      const originalKeyword = keywordString.split(',')
        .map(k => k.trim())
        .find(k => k.toLowerCase() === uniqueKeyword);
      return originalKeyword;
    }).filter(Boolean);
  };

  // ADDED: Sync rowsPerPage with paginationInfo when it changes
  useEffect(() => {
    if (paginationInfo?.pageSize && paginationInfo.pageSize !== rowsPerPage) {
      setRowsPerPage(paginationInfo.pageSize);
    }
  }, [paginationInfo?.pageSize]);

  // ADDED: Fetch existing social media for all affiliates when data loads
  // This allows sharing social media found in Contact Finder across all modules
  useEffect(() => {
    const fetchExistingSocialMedia = async () => {
      if (!data || data.length === 0) return;

      // Get all affiliate URLs from the current data
      const affiliateUrls = data
        .map(row => row.affiliate_url)
        .filter(Boolean)
        .map(url => url.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0]);

      // Only fetch for URLs we don't already have social media for
      const urlsToFetch = affiliateUrls.filter(url => {
        // Check if any row with this URL already has social media
        const rowId = data.find(r => {
          const rowUrl = r.affiliate_url?.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
          return rowUrl === url;
        })?.id;
        return rowId && !socialMediaResults[rowId];
      });

      if (urlsToFetch.length === 0) return;

      // Remove duplicates
      const uniqueUrls = [...new Set(urlsToFetch)];

      try {
        const response = await fetch(`${basePath}/api/contact-finder/social/batch`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ domains: uniqueUrls })
        });

        const result = await response.json();

        if (result.success && result.results) {
          // Map results back to row IDs
          const newSocialMedia = {};

          data.forEach(row => {
            if (!row.affiliate_url) return;
            const rowUrl = row.affiliate_url.toLowerCase()
              .replace(/^https?:\/\//, '')
              .replace(/^www\./, '')
              .split('/')[0];

            if (result.results[rowUrl]) {
              newSocialMedia[row.id] = result.results[rowUrl];
            }
          });

          if (Object.keys(newSocialMedia).length > 0) {
            setSocialMediaResults(prev => ({ ...prev, ...newSocialMedia }));
          }
        }
      } catch (error) {
        console.error('[UniversalTable] Error fetching existing social media:', error);
      }
    };

    fetchExistingSocialMedia().catch(console.error);
  }, [data, basePath]); // Re-run when data changes

  // Pull the pre-computed contacts for whatever is on screen, in one request.
  //
  // These were gathered offline and cost nothing to read, so this runs eagerly
  // on every page rather than waiting for a click. Domains the contacts script
  // has not reached yet come back absent, and those rows keep the existing
  // per-row "Find Contact" button untouched -- this is purely additive.
  useEffect(() => {
    const fetchStoredContacts = async () => {
      if (!data || data.length === 0) return;

      const rowsNeeding = data.filter(row => row.affiliate_url && !storedContacts[row.id]);
      if (rowsNeeding.length === 0) return;

      const domains = [...new Set(rowsNeeding.map(row => row.affiliate_url))];

      try {
        const response = await fetch(`${basePath}/api/affiliate-recruitment/contacts/batch`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ domains })
        });

        if (!response.ok) return;
        const result = await response.json();
        if (!result.success || !result.results) return;

        // The endpoint keys results on the bare host, so normalise the row's URL
        // the same way before looking it up.
        const byRow = {};
        data.forEach(row => {
          if (!row.affiliate_url) return;
          const host = String(row.affiliate_url).toLowerCase()
            .replace(/^https?:\/\//, '')
            .replace(/^www\./, '')
            .split('/')[0];
          if (result.results[host]) byRow[row.id] = result.results[host];
        });

        if (Object.keys(byRow).length > 0) {
          setStoredContacts(prev => ({ ...prev, ...byRow }));
        }
      } catch (error) {
        // A failure here must not take the table down with it; the rows simply
        // behave as they did before this feature existed.
        console.error('[UniversalTable] Error fetching stored contacts:', error);
      }
    };

    fetchStoredContacts().catch(console.error);
    // storedContacts is intentionally not a dependency: it is written by this
    // effect, and including it would re-run the fetch on its own result.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, basePath]);

  // Fetch user brands for pipeline brand selection
  useEffect(() => {
    const fetchUserBrands = async () => {
      try {
        const response = await fetch(`${basePath}/api/crm/brands`, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        });
        const result = await response.json();
        if (result.success && result.data) {
          setUserBrands(result.data);
        }
      } catch (error) {
        console.error('[UniversalTable] Error fetching brands:', error);
      }
    };
    fetchUserBrands().catch(console.error);
  }, [basePath]);

  // Check CRM pipeline status for current data
  useEffect(() => {
    const checkPipelineStatuses = async () => {
      if (!data || data.length === 0) return;
      const urls = data.map(item => item.affiliate_url).filter(Boolean);
      if (urls.length === 0) return;

      try {
        const response = await fetch(`${basePath}/api/crm/check-status`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ affiliate_urls: urls })
        });
        const result = await response.json();
        if (result.success) {
          setPipelineStatuses(result.data);
        }
      } catch (error) {
        console.error('[UniversalTable] Error checking pipeline statuses:', error);
      }
    };

    checkPipelineStatuses().catch(console.error);
  }, [data, basePath]);

  // Which of the rows on screen this person marked approached, and for which brands.
  useEffect(() => {
    if (!approachable || !data || data.length === 0) return;
    const urls = [...new Set(data.map(item => item.affiliate_url).filter(Boolean))];
    if (urls.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${basePath}/api/affiliate-recruitment/approached/status`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ affiliate_urls: urls })
        });
        const result = await response.json();
        if (!cancelled && result.success && result.data) {
          setApproachedStatus(prev => ({ ...prev, ...result.data }));
        }
      } catch (error) {
        console.error('[UniversalTable] Error checking approached status:', error);
      }
    })();
    return () => { cancelled = true; };
  }, [approachable, data, basePath]);

  // The row's approached state: marked for a brand by this person, or emailed from MARS.
  const approachedOf = (affiliateUrl) => {
    const status = approachedStatus[affiliateUrl] || { brands: [], emailed: false };
    return { ...status, isApproached: approachable && (status.brands.length > 0 || status.emailed) };
  };

  // Mark the site approached for a brand, or take the mark back when it has one.
  const handleToggleApproached = async (affiliateUrl, brand) => {
    const current = approachedStatus[affiliateUrl]?.brands || [];
    const marked = current.some(b => b.brand_id === brand.id);
    setSavingApproach(brand.id);
    setApproachError('');
    try {
      const response = await fetch(`${basePath}/api/affiliate-recruitment/approached${marked ? '/remove' : ''}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ affiliate_url: affiliateUrl, brand_id: brand.id })
      });
      const result = await response.json();
      if (!result.success) {
        setApproachError(result.message || 'Could not save that. Please try again.');
        return;
      }
      setApproachedStatus(prev => {
        const entry = prev[affiliateUrl] || { brands: [], emailed: false };
        const brands = marked
          ? entry.brands.filter(b => b.brand_id !== brand.id)
          : [...entry.brands, { brand_id: brand.id, brand_name: brand.brand_name || brand.brand_url }];
        return { ...prev, [affiliateUrl]: { ...entry, brands } };
      });
      if (onApproachedChange) onApproachedChange(affiliateUrl);
    } catch (error) {
      console.error('[UniversalTable] Error saving approached:', error);
      setApproachError('Could not save that. Please try again.');
    } finally {
      setSavingApproach(null);
    }
  };

  // Show brand selection popup when clicking Prospect
  const handleProspectClick = (e, affiliateUrl, email, prospectId, prospectBrandId) => {
    e.stopPropagation();
    e.preventDefault();
    setBrandPopup({
      visible: true,
      affiliateUrl,
      email,
      prospectId: prospectId || null,
      prospectBrandId: prospectBrandId || null
    });
  };

  // Close brand popup on outside click (with delay to avoid immediate close)
  useEffect(() => {
    if (!brandPopup.visible) return;
    const handleClickOutside = (e) => {
      const popupEl = document.getElementById('brand-pipeline-popup');
      if (popupEl && popupEl.contains(e.target)) return;
      setBrandPopup(prev => ({ ...prev, visible: false }));
    };
    // Use setTimeout to avoid the current click event triggering close
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 10);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [brandPopup.visible]);

  // Remove affiliate from CRM pipeline for a specific brand
  const handleRemoveFromPipeline = async (pipelineId, affiliateUrl) => {
    try {
      const response = await fetch(`${basePath}/api/crm/${pipelineId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      const result = await response.json();
      if (result.success) {
        // Remove entry from local state
        setPipelineStatuses(prev => {
          const existing = prev[affiliateUrl] || [];
          return {
            ...prev,
            [affiliateUrl]: existing.filter(e => e.id !== pipelineId)
          };
        });
      }
    } catch (error) {
      console.error('[UniversalTable] Error removing from pipeline:', error);
    }
    setConfirmingRemove(null);
  };

  // Add affiliate to CRM pipeline with brand_id
  const handleAddToPipeline = async (affiliateUrl, email, brandId) => {
    setBrandPopup(prev => ({ ...prev, visible: false }));
    setAddingToPipeline(prev => ({ ...prev, [affiliateUrl]: true }));
    try {
      const response = await fetch(`${basePath}/api/crm/add`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          affiliate_url: affiliateUrl,
          email: email || null,
          source: title || 'affiliate-table',
          brand_id: brandId
        })
      });
      const result = await response.json();
      if (result.success || result.data) {
        const newEntry = {
          stage: result.data?.stage || 'prospected',
          id: result.data?.id,
          brand_id: result.data?.brand_id || brandId,
          brand_name: result.data?.brand_name
        };
        setPipelineStatuses(prev => {
          const existing = prev[affiliateUrl] || [];
          // Check if this brand already exists
          const alreadyExists = existing.some(e => e.brand_id === newEntry.brand_id);
          return {
            ...prev,
            [affiliateUrl]: alreadyExists ? existing : [...existing, newEntry]
          };
        });
      }
    } catch (error) {
      console.error('[UniversalTable] Error adding to pipeline:', error);
    } finally {
      setAddingToPipeline(prev => ({ ...prev, [affiliateUrl]: false }));
    }
  };

  // Truncated, not rounded, so an abbreviation never reads higher than the real
  // figure: 1,589 is 1.5K, not 1.6K. Matches the dashboard's Paid Traffic Finder.
  // Divided as integers because 2900/1000*10 is 28.999999999999996 in floating
  // point, which would floor to 2.8K.
  const formatNumber = (num) => {
    const n = Number(num) || 0;
    if (n >= 1000000) {
      return (Math.floor(n / 100000) / 10).toFixed(1) + 'M';
    }
    if (n >= 1000) {
      return (Math.floor(n / 100) / 10).toFixed(1) + 'K';
    }
    return n.toLocaleString();
  };

  const getProcessedGeos = (geoData) => {
    if (!geoData) return [];

    // Countries to exclude from display
    const excludedCountries = ['unknown', 'xx', 'mobile'];

    const trimGeo = geoData.split(',');

    return trimGeo
      .map((trimgeo) => {
        const [country, count] = trimgeo.split(':');
        const trimmedCountry = country?.trim();

        // Skip if country is empty or in excluded list
        if (!trimmedCountry || excludedCountries.includes(trimmedCountry.toLowerCase())) {
          return null;
        }

        const shortCountryName = countryList(trimmedCountry) || trimmedCountry;

        // Also check if the short country name is in excluded list (e.g., "XX")
        if (excludedCountries.includes(shortCountryName.toLowerCase())) {
          return null;
        }

        return {
          country: shortCountryName,
          count: count?.trim() || '0'
        };
      })
      .filter(Boolean);
  };

  const formatTopGeos = (geoData) => {
    if (!geoData) return [];

    // Countries to exclude from display
    const excludedCountries = ['unknown', 'xx', 'mobile'];

    const trimGeo = geoData.split(',');

    // Filter out excluded countries and process the data
    const processedGeos = trimGeo
      .map((trimgeo) => {
        const [country, count] = trimgeo.split(':');
        const trimmedCountry = country?.trim();

        // Skip if country is empty or in excluded list
        if (!trimmedCountry || excludedCountries.includes(trimmedCountry.toLowerCase())) {
          return null;
        }

        // Get the short country code using countryList function
        const shortCountryName = countryList(trimmedCountry) || trimmedCountry;

        // Also check if the short country name is in excluded list (e.g., "XX")
        if (excludedCountries.includes(shortCountryName.toLowerCase())) {
          return null;
        }

        return {
          country: shortCountryName,
          count: count?.trim() || '0'
        };
      })
      .filter(Boolean); // Remove null entries
    
    // Show only first 2 countries
    const displayGeos = processedGeos.slice(0, 2);
    const remainingCount = processedGeos.length - 2;
    
    const geoElements = displayGeos.map((geo, index) => (
      <span key={index} className='mr-1'>
        <span className="text-gray-900 dark:text-slate-200 font-normal text-xs mr-1">{geo.country}</span>
        <span className="text-blue-600 dark:text-blue-400 font-semibold text-xs">[{formatNumber(geo.count)}]</span>
      </span>
    ));

    // Add comma separator and remaining count
    const result = [];
    geoElements.forEach((element, index) => {
      result.push(element);
      if (index < geoElements.length - 1) {
        result.push(<span key={`comma-${index}`} className="text-gray-400 dark:text-slate-500 text-xs">, </span>);
      }
    });

    // Add "+X more" if there are more countries
    if (remainingCount > 0) {
      result.push(<span key="comma-more" className="text-gray-400 dark:text-slate-500 text-xs">, </span>);
      result.push(
        <span key="more" className="text-gray-500 dark:text-slate-400 font-normal text-xs">
          +{remainingCount} more
        </span>
      );
    }

    return result;
  };

  // Handle email popup opening
  const handleEmailClick = (emailData) => {
    setSelectedEmailData(emailData);
    setEmailPopupVisible(true);
  };

  // Get pagination info (either from server or local) - MOVED UP BEFORE shouldShowData
  // Note: For client-side pagination, we use state-based pagination info
  // The table reference will be used after table is initialized for client-side mode
  const [clientPaginationState, setClientPaginationState] = useState({
    pageIndex: 0,
    pageSize: defaultPageSize
  });

  const currentPaginationInfo = useMemo(() => {
    if (paginationInfo) return paginationInfo;

    // For client-side pagination, use the state-based pagination
    const totalRows = data.length;
    const totalPages = Math.ceil(totalRows / clientPaginationState.pageSize);

    return {
      page: clientPaginationState.pageIndex,
      pageSize: clientPaginationState.pageSize,
      total: totalRows,
      totalPages,
      hasNextPage: clientPaginationState.pageIndex < totalPages - 1,
      hasPreviousPage: clientPaginationState.pageIndex > 0
    };
  }, [paginationInfo, data.length, clientPaginationState]);

  // Function to check if row should show data or image based on plan.
  // The plan sets the default ceiling (Free 0 / Growth 25 / Pro 500 /
  // Enterprise unlimited); users.row_limit can raise or lower it for one
  // account without a code change. See services/planRowLimit.js.
  const shouldShowData = (rowIndex) => {
    // Calculate the global row index across all pages
    const globalRowIndex = (currentPaginationInfo.page * currentPaginationInfo.pageSize) + rowIndex;

    if (!userPlan || userPlan === '') {
      return false; // Default to Free plan behavior if no plan
    }

    return isRowVisible(globalRowIndex, userPlan, effectiveRowLimit);
  };

  // Only the Forex vertical ever populates deal_types, so this stays false —
  // and the Deal column stays out of the array entirely — for every table
  // showing gambling rows, which is every table today. See DealDetailsPopup
  // above for the column contract.
  const showDealColumn = data.some((r) => r && r.deal_types);

  // Same contract as showDealColumn: the licence crawler has classified only
  // part of affiliate_clean so far, so a page of rows it has not reached yet
  // renders exactly as it did before this column existed.
  const showRegulatorColumn = data.some((r) => r && r.licence_authorities);

  // Generate columns dynamically from data or use custom columns
  const columns = [
    columnHelper.display({
      id: 'action',
      header: 'Action',
      cell: ({ row }) => {
        const rowIndex = row.index;
        
        if (!shouldShowData(rowIndex)) {
          return null; // Don't show action buttons for image rows
        }
        
        // Already approached sits here, next to the +, so it is seen without
        // scrolling right; the row itself is highlighted too (see the <tr>).
        const affiliateUrl = row.original.affiliate_url;
        const approached = approachable && affiliateUrl ? approachedOf(affiliateUrl) : null;
        const approachedLabel = approached && [
          ...approached.brands.map(b => b.brand_name),
          ...(approached.emailed ? ['Emailed'] : []),
        ].join(', ');

        return (
          <div className="flex flex-col items-center gap-1">
            <div className="flex items-center justify-center space-x-2">
              <button
                className="text-blue-600 hover:text-blue-800 transition-colors"
                onClick={() => row.toggleExpanded()}
              >
                {row.getIsExpanded() ? (
                  <ChevronDown className='w-4 h-4'/>
                ) : (
                  <Plus className='w-4 h-4'/>
                )}
              </button>
              {approached && (
                <button
                  className={`text-[10px] font-semibold py-0.5 px-2 rounded-lg transition-all duration-200 flex items-center gap-1 border ${
                    approached.isApproached
                      ? 'bg-amber-500 border-amber-500 text-white hover:bg-amber-600'
                      : 'border-gray-300 dark:border-slate-600 text-gray-600 dark:text-slate-300 hover:border-amber-400 hover:text-amber-700 dark:hover:text-amber-300'
                  }`}
                  title={approached.isApproached ? `Approached: ${approachedLabel}. Click to edit.` : 'Mark as already approached'}
                  onClick={(e) => {
                    e.stopPropagation();
                    setApproachError('');
                    setApproachPopup({ visible: true, affiliateUrl });
                  }}
                >
                  {approached.isApproached ? <Check className="w-3 h-3" /> : <UserCheck className="w-3 h-3" />}
                  {approached.isApproached ? 'Approached' : 'Approach'}
                </button>
              )}
            </div>
            {approached?.isApproached && (
              <span className="text-[9px] font-medium text-amber-700 dark:text-amber-300 max-w-[140px] truncate" title={approachedLabel}>
                {approachedLabel}
              </span>
            )}
          </div>
        );
      },
      enableHiding: false,
      size: 100,
    }),
    
    columnHelper.accessor('affiliate_url', {
      id: 'affiliate_url',
      header: 'Affiliate URL',
      cell: ({ getValue, row }) => {
        const rowIndex = row.index;

        if (!shouldShowData(rowIndex)) {
          return null;
        }

        const rawUrl = getValue();
        const confidenceUrl = row.original.affiliate_url_confidence;

        // Extract root domain for Google search
        const getRootDomain = (url) => {
          if (!url) return '';
          return url.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
        };

        // Create Google search URL with gambling keywords (for main affiliate URL)
        const createGoogleSearchUrl = (url) => {
          const rootDomain = getRootDomain(url);
          if (!rootDomain) return null;
          const searchQuery = `site:${rootDomain} (casino OR poker OR sport OR betting)`;
          return `https://www.google.com/search?q=${encodeURIComponent(searchQuery)}`;
        };

        // Truncate URL for display - remove protocol and show path truncated
        const truncateConfidenceUrl = (url, maxLength = 35) => {
          if (!url) return '';
          // Remove protocol
          const cleanUrl = url.replace(/^https?:\/\//, '').replace(/^www\./, '');
          if (cleanUrl.length <= maxLength) return cleanUrl;
          return cleanUrl.substring(0, maxLength) + '...';
        };

        // Format confidence URL for linking (ensure it has protocol)
        const formatConfidenceUrl = (url) => {
          if (!url) return '';
          if (url.startsWith('http://') || url.startsWith('https://')) return url;
          return `https://${url}`;
        };

        // Format URL for direct link to root domain
        const formatRootDomainUrl = (url) => {
          if (!url) return '';
          if (url.startsWith('http://') || url.startsWith('https://')) return url;
          return `https://${url}`;
        };

        // Paid-activity marker. Roughly 0.5% of affiliate_clean has any paid
        // activity, so this is a rare, high-signal flag rather than decoration.
        // Same component powers Affiliate Recruitment, Affiliate Directory and
        // Untapped Opportunities.
        //
        // Deliberately just a letter. It used to be a pill reading "PAID 1.8K"
        // followed by the keyword, which repeated the Paid Traffic column two
        // cells to the right and pushed a long keyword into the URL column. The
        // detail moved to the tooltip and the expanded row, both of which have
        // room for it; scanning a list only needs to know THAT a row advertises
        // and WHERE.
        const paidTraffic = Number(row.original.paid_traffic) || 0;
        const paidKeywords = getUniqueKeywords(row.original.paid_keyword);
        const activity = row.original.paid_activity || null;

        const liveChannels = resolvePaidChannels(row.original.paid_channels, paidTraffic);
        // Channels they have ever been seen on, for affiliates whose ads have
        // since stopped. A dormant advertiser is still worth flagging.
        const pastChannels = (activity?.channels || []).filter((c) => !liveChannels.includes(c));

        const isCurrent = liveChannels.length > 0 || paidTraffic > 0 || paidKeywords.length > 0;
        const markerChannels = (liveChannels.length > 0 ? liveChannels : pastChannels)
          .map(channelMeta)
          .filter(Boolean);

        const hasPaidActivity = isCurrent || pastChannels.length > 0;

        const paidTagTitle = [
          isCurrent
            ? `Advertising${liveChannels.length ? ` on ${describeChannels(liveChannels)}` : ''}${paidTraffic > 0 ? ` — ${formatNumber(paidTraffic)} paid visits` : ''}`
            : `Was advertising${pastChannels.length ? ` on ${describeChannels(pastChannels)}` : ''}${activity?.last_seen_at ? ` — last seen ${new Date(activity.last_seen_at).toLocaleDateString()}` : ''}`,
          paidKeywords.length > 0 ? `\n\nKeywords:\n${paidKeywords.slice(0, 8).join('\n')}` : '',
          activity?.evidence?.length
            ? `\n\nRecorded finds:\n${activity.evidence
                .map((e) => `${e.label}${e.country ? ` (${e.country})` : ''} — ${new Date(e.seen_at).toLocaleDateString()}${e.status === 'inactive' ? ' · ad inactive' : ''}`)
                .join('\n')}`
            : '',
          '\n\nExpand the row for the full paid advertising record.'
        ].join('');

        return (
          <div className="text-blue-600 dark:text-blue-400 font-medium">
            <a
              href={formatRootDomainUrl(rawUrl)}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-blue-800 dark:hover:text-blue-300 hover:underline transition-colors"
              title={rawUrl}
            >
              {rawUrl}
            </a>

            {hasPaidActivity && (
              // Inline with the URL, not on its own line — the marker is small
              // enough that a second line would be all whitespace.
              <span className="ml-1.5 inline-flex items-center align-middle" title={paidTagTitle}>
                {markerChannels.length === 0 ? (
                  // Paid activity with no channel resolved — an endpoint that
                  // does not send paid_channels. Flag it without claiming where.
                  <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-[9px] font-bold leading-none text-white">
                    P
                  </span>
                ) : isCurrent ? (
                  <span className="inline-flex items-center gap-0.5">
                    {markerChannels.map((channel) => (
                      <span
                        key={channel.key}
                        className={`inline-flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-bold leading-none ${channel.solid}`}
                      >
                        {channel.letter}
                      </span>
                    ))}
                  </span>
                ) : (
                  // Ads have stopped. A greyed-out letter alone was too easy to
                  // read as just a paler version of the live marker, so the past
                  // state says so in words — still one line, still a few pixels
                  // wider than the letter it replaces.
                  <span className="inline-flex items-center gap-0.5 rounded-full bg-gray-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase leading-none tracking-wide text-gray-500 ring-1 ring-inset ring-gray-200 dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-600">
                    was
                    {markerChannels.map((channel) => (
                      <span key={channel.key} className="font-bold">
                        {channel.letter}
                      </span>
                    ))}
                  </span>
                )}
              </span>
            )}
          </div>
        );
      },
      enableSorting: true,
    }),

    columnHelper.accessor('total_traffic', {
      id: 'total_traffic',
      header: 'Total Traffic',
      cell: ({ getValue, row }) => {
        const rowIndex = row.index;

        if (!shouldShowData(rowIndex)) {
          return null;
        }

        return (
          <div className="text-blue-600 dark:text-blue-400 font-medium">{formatNumber(getValue())}</div>
        );
      },
      enableSorting: true,
    }),

    columnHelper.accessor('organic_traffic', {
      id: 'organic_traffic',
      header: 'Organic Traffic',
      cell: ({ getValue, row }) => {
        const rowIndex = row.index;

        if (!shouldShowData(rowIndex)) {
          return null;
        }

        return (
          <div className="text-gray-900 dark:text-slate-200">{formatNumber(getValue())}</div>
        );
      },
      enableSorting: true,
    }),

    columnHelper.accessor('paid_traffic', {
      id: 'paid_traffic',
      header: 'Paid Traffic',
      cell: ({ getValue, row }) => {
        const rowIndex = row.index;

        if (!shouldShowData(rowIndex)) {
          return null;
        }

        return (
          <div className="text-blue-600 dark:text-blue-400 font-medium">{formatNumber(getValue())}</div>
        );
      },
      enableSorting: true,
    }),

    columnHelper.accessor('top_geo_organic_traffic', {
      id: 'top_geo_organic_traffic',
      header: 'Top Geos',
      cell: ({ getValue, row }) => {
        const rowIndex = row.index;

        if (!shouldShowData(rowIndex)) {
          return null;
        }

        return (
          <div
            className='text-gray-900 font-medium flex justify-center items-center cursor-pointer relative'
            onMouseEnter={(e) => {
              const allGeos = getProcessedGeos(getValue());
              showTooltip(allGeos, e);
            }}
            onMouseLeave={() => {
              hideTooltip();
            }}
          >
           {formatTopGeos(getValue())}
          </div>
        );
      },
      enableSorting: true,
    }),

    columnHelper.accessor('vertical', {
      id: 'vertical',
      header: 'Vertical',
      cell: ({ getValue, row }) => {
        const rowIndex = row.index;

        if (!shouldShowData(rowIndex)) {
          return null;
        }

        const vertical = getValue();
        if (!vertical) return <span className="text-gray-400 dark:text-slate-500 text-xs">-</span>;

        // Vertical mapping - supports both legacy numeric and new text format
        const verticalMap = {
          // Legacy numeric values
          '1': 'Casino',
          '2': 'Sports Betting',
          '3': 'Lottery',
          '4': 'Poker',
          '5': 'Bingo',
          '6': 'Sweepstake',
          // New text values from DB
          'casino': 'Casino',
          'sports_betting': 'Sports Betting',
          'poker': 'Poker',
          'lottery': 'Lottery',
          'bingo': 'Bingo',
          'sweepstake': 'Sweepstake',
          'crypto': 'Crypto'
        };

        // Handle multiple verticals (comma separated)
        const allVerticals = String(vertical).split(',').map(v => v.trim()).filter(Boolean);

        // Scope to the brand's own vertical when the page knows it. Matching is
        // the same normalisation the backend gate uses (lowercase, underscores
        // to spaces) plus the two spellings that reach affiliate_clean from
        // different ingest paths — 'Forex Affiliates' is the pre-rename name of
        // 'Trading & Investment' and must keep resolving to it.
        const vertKey = (v) => {
          const n = String(v || '').trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
          if (n === 'forex affiliates') return 'trading & investment';
          if (n === 'sportsbetting') return 'sports betting';
          if (n === 'sweepstakes') return 'sweepstake';
          return n;
        };
        const scope = String(brandVertical || '').split(',').map(vertKey).filter(Boolean);
        const scoped = scope.length > 0
          ? allVerticals.filter(v => scope.includes(vertKey(v)))
          : [];
        // Falling back to the full list when the row shares nothing with the
        // brand keeps the column from rendering empty — better to show what the
        // row actually says than nothing at all.
        const verticals = scoped.length > 0 ? scoped : allVerticals;

        // Sub-categories under the vertical — the forex instruments (CFDs,
        // Shares, ETFs, ...). Comma-joined like `vertical` itself, NULL on
        // every gambling row, so this renders nothing at all for them. Shown
        // beneath the vertical chips and in a lighter tone, because it reads as
        // a qualifier on the vertical rather than a second vertical.
        const subVerticals = String(row.original?.sub_vertical || '')
          .split(',').map(sv => sv.trim()).filter(Boolean);

        return (
          <div className="flex flex-col items-center gap-1 max-w-[140px]">
            <div className="flex flex-wrap gap-1 justify-center">
              {verticals.map((v, idx) => {
                const vLower = v.toLowerCase();
                const name = verticalMap[v] || verticalMap[vLower] || v;
                return (
                  <span key={idx} className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300">
                    {name}
                  </span>
                );
              })}
            </div>
            {subVerticals.length > 0 && (
              <div className="flex flex-wrap gap-1 justify-center">
                {subVerticals.map((sv, idx) => (
                  <span key={idx} className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-slate-100 dark:bg-slate-700/60 text-slate-600 dark:text-slate-300">
                    {sv}
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      },
      enableSorting: true,
    }),

    // Regulator column — the licence authorities behind the brands this
    // affiliate promotes, and the verdict over them. Spliced in only when a row
    // on this page actually carries them (see showRegulatorColumn).
    ...(showRegulatorColumn ? [columnHelper.accessor('licence_authorities', {
      id: 'licence_authorities',
      header: 'Licence',
      cell: ({ getValue, row }) => {
        const rowIndex = row.index;
        if (!shouldShowData(rowIndex)) return null;

        const authorities = String(getValue() || '')
          .split(',').map((a) => a.trim()).filter(Boolean);
        const status = LICENCE_STATUS_META[row.original?.licence_status] || null;

        // A row the crawler has not reached, on a page where other rows have
        // been. Deliberately a dash rather than 'Unknown': we have not looked,
        // which is not the same as having looked and found nothing.
        if (authorities.length === 0 && !status) {
          return <span className="text-gray-400 dark:text-slate-500 text-xs">-</span>;
        }

        return (
          <div className="flex flex-col items-center gap-1 max-w-[150px]">
            <div className="flex flex-wrap gap-1 justify-center">
              {authorities.slice(0, 4).map((authority, idx) => (
                <span key={idx} className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300">
                  {authority}
                </span>
              ))}
              {authorities.length > 4 && (
                <span
                  className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400"
                  title={authorities.join(', ')}
                >
                  +{authorities.length - 4}
                </span>
              )}
            </div>
            {status && (
              <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${status.className}`}>
                {status.label}
              </span>
            )}
          </div>
        );
      },
      enableSorting: false,
    })] : []),

    // Forex `Deal` column — only spliced into the array below when at least
    // one row on this page carries deal_types (see showDealColumn). Every
    // gambling row has entity_type/deal_types = null and renders the "-"
    // placeholder if this column is ever shown alongside forex rows.
    ...(showDealColumn ? [columnHelper.display({
      id: 'deal',
      header: 'Deal',
      cell: ({ row }) => {
        const rowIndex = row.index;
        if (!shouldShowData(rowIndex)) return null;

        const original = row.original;
        const dealTypes = String(original.deal_types || '').split(',').map((d) => d.trim()).filter(Boolean);
        const entityMeta = ENTITY_TYPE_META[original.entity_type] || null;
        const stance = original.deal_details?.stance_source;

        if (!entityMeta && dealTypes.length === 0) {
          return <span className="text-gray-400 dark:text-slate-500 text-xs">-</span>;
        }

        return (
          <button
            type="button"
            className="flex flex-col items-center gap-1 mx-auto group"
            onClick={() => setDealPopup({ visible: true, row: original })}
            title="View deal details"
          >
            {entityMeta && (
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${entityMeta.className}`}>
                {entityMeta.label}
              </span>
            )}
            {dealTypes.length > 0 && (
              <div className="flex flex-wrap gap-1 justify-center max-w-[130px]">
                {dealTypes.slice(0, 3).map((dt, idx) => (
                  <span key={idx} className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 group-hover:bg-emerald-200 dark:group-hover:bg-emerald-900/60 transition-colors">
                    {dt}
                  </span>
                ))}
                {dealTypes.length > 3 && (
                  <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400">
                    +{dealTypes.length - 3}
                  </span>
                )}
              </div>
            )}
            {stance && (
              <span className={`text-[9px] font-semibold ${stance === 'advertised' ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-slate-500'}`}>
                {stance === 'advertised' ? 'Advertised' : 'Inferred'}
              </span>
            )}
          </button>
        );
      },
      enableSorting: false,
    })] : []),

    columnHelper.accessor('email', {
      id: 'email',
      header: 'Contact',
      cell: ({ getValue, row }) => {
        const rowIndex = row.index;

        if (!shouldShowData(rowIndex)) {
          return null;
        }

        // Check if we found contacts for this row
        const foundContacts = contactResults[row.id];
        const socialMedia = socialMediaResults[row.id];
        const email = foundContacts && foundContacts !== 'NO_CONTACTS' && foundContacts !== 'ERROR' ? foundContacts : getValue();
        const emailCount = email ? new Set(email.split(',').map(e => e.trim()).filter(Boolean)).size : 0;
        const isLoading = findingContact[row.id];
        const affiliateUrl = row.original.affiliate_url;

        // Count social handles
        const socialCount = socialMedia ? Object.values(socialMedia).flat().filter(Boolean).length : 0;

        // Pre-computed contacts for this site, if the offline script has reached
        // it. This counts ALL four channels -- a site reachable only by contact
        // form or Telegram used to read "No Contact" because the count above
        // only ever looked at email.
        const stored = storedContacts[row.id];
        const storedCount = stored ? stored.total || 0 : 0;

        // What a "Find Contact" lookup found when that included the authors.
        const lookup = lookupResults[row.id];
        const authorCount = lookup ? lookup.authors.length : 0;
        const isPending = pendingLookups[row.id];

        const getStatusText = () => {
          if (isPending) return 'Still searching, try again in a minute';
          if (authorCount > 0) {
            return [
              emailCount > 0 && `${emailCount} Email${emailCount === 1 ? '' : 's'}`,
              `${authorCount} Author${authorCount === 1 ? '' : 's'}`
            ].filter(Boolean).join(', ') + ' Found';
          }
          if (foundContacts === 'NO_CONTACTS' || foundContacts === 'ERROR') {
            if (socialCount > 0) return `${socialCount} Social Found`;
            return 'No Contact Found';
          }
          if (emailCount === 0 && socialCount > 0) return `${socialCount} Social Found`;
          if (emailCount === 0) return 'No Contact';
          if (emailCount === 1) return '1 Email Found';
          return `${emailCount} Contacts Found`;
        };

        return (
          <div className="text-blue-600 dark:text-blue-400 font-medium text-center">
            {storedCount > 0 ? (
              // We already hold every channel for this site: open the picker
              // rather than firing a paid lookup or jumping straight to email.
              <button
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 transform hover:scale-105 hover:shadow-lg shadow-blue-600/30 text-white text-xs font-medium py-1 px-3 rounded-lg transition-all duration-300 flex items-center gap-1 mx-auto"
                onClick={() => setContactPopup({
                  visible: true,
                  domain: affiliateUrl,
                  contacts: stored.contacts || [],
                  scan: stored.scan || null
                })}
                title={Object.entries(stored.counts || {})
                  .map(([ch, n]) => `${n} ${ch.replace('_', ' ')}`)
                  .join(', ')}
              >
                <Mail className="w-3 h-3" />
                Contact ({storedCount})
              </button>
            ) : authorCount > 0 ? (
              // The lookup found the people behind the site: reopen the list
              // that shows them, emails included.
              <button
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 transform hover:scale-105 hover:shadow-lg shadow-blue-600/30 text-white text-xs font-medium py-1 px-3 rounded-lg transition-all duration-300 flex items-center gap-1 mx-auto"
                onClick={() => setContactPopup({ visible: true, domain: affiliateUrl, scan: null, ...lookup })}
              >
                <Mail className="w-3 h-3" />
                Contact ({emailCount + authorCount})
              </button>
            ) : emailCount > 0 ? (
              // Has contacts - show Contact button
              <button
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 transform hover:scale-105 hover:shadow-lg shadow-blue-600/30 text-white text-xs font-medium py-1 px-3 rounded-lg transition-all duration-300 flex items-center gap-1 mx-auto"
                onClick={() => handleEmailClick(email)}
              >
                <Mail className="w-3 h-3" />
                Contact
              </button>
            ) : (
              // No contacts - show Find Contact button (blue color)
              <button
                className={`${isLoading
                  ? 'bg-blue-400 cursor-wait'
                  : (foundContacts === 'NO_CONTACTS' || foundContacts === 'ERROR') && socialCount === 0
                    ? 'bg-gray-400 dark:bg-slate-600 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 transform hover:scale-105 hover:shadow-lg shadow-blue-500/30'
                } text-white text-xs font-medium py-1 px-3 rounded-lg transition-all duration-300 flex items-center gap-1 mx-auto`}
                onClick={() => !isLoading && !(foundContacts === 'NO_CONTACTS' && socialCount === 0) && handleFindContact(affiliateUrl, row.id)}
                disabled={isLoading || ((foundContacts === 'NO_CONTACTS' || foundContacts === 'ERROR') && socialCount === 0)}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Finding...
                  </>
                ) : (
                  <>
                    <UserSearch className="w-3 h-3" />
                    Find Contact
                  </>
                )}
              </button>
            )}
            {/* Social media icons below button */}
            {socialMedia && socialCount > 0 && (
              <div className="flex items-center justify-center gap-1 mt-1.5">
                {socialMedia.linkedin && socialMedia.linkedin.length > 0 && (
                  <a href={socialMedia.linkedin[0]} target="_blank" rel="noopener noreferrer"
                     className="p-1 rounded bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors">
                    <Linkedin className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                  </a>
                )}
                {socialMedia.twitter && socialMedia.twitter.length > 0 && (
                  <a href={socialMedia.twitter[0]} target="_blank" rel="noopener noreferrer"
                     className="p-1 rounded bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors">
                    <Twitter className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                  </a>
                )}
                {socialMedia.facebook && socialMedia.facebook.length > 0 && (
                  <a href={socialMedia.facebook[0]} target="_blank" rel="noopener noreferrer"
                     className="p-1 rounded bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors">
                    <Facebook className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                  </a>
                )}
                {socialMedia.instagram && socialMedia.instagram.length > 0 && (
                  <a href={socialMedia.instagram[0]} target="_blank" rel="noopener noreferrer"
                     className="p-1 rounded bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors">
                    <Instagram className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                  </a>
                )}
              </div>
            )}
            {/* Status line. Suppressed once we already hold stored contacts:
                the button itself reads "Contact (n)", and getStatusText() only
                looks at email/social, so it wrongly said "No Contact" for sites
                reachable by contact form or Telegram. */}
            {storedCount === 0 && (
              <div className="text-[10px] mt-1 text-gray-500 dark:text-slate-400">
                {getStatusText()}
              </div>
            )}
          </div>
        );
      },
      enableSorting: true,
    }),

    // CRM Pipeline column - separate column for adding affiliates to pipeline
    columnHelper.display({
      id: 'pipeline',
      header: () => (
        <div className="flex items-center justify-center gap-1">
          <Target className="w-4 h-4 text-green-600" />
          <span className="text-xs font-medium text-green-600 dark:text-green-400 tracking-wider">Pipeline</span>
        </div>
      ),
      cell: ({ row }) => {
        const rowIndex = row.index;
        if (!shouldShowData(rowIndex)) return null;

        const affiliateUrl = row.original.affiliate_url;
        const email = row.original.email;
        const pipelineEntries = pipelineStatuses[affiliateUrl]; // Now an array

        const stageColors = {
          prospected: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
          contacted: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
          negotiating: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
          onboarding: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
          active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
          churned: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
        };

        return (
          <div className="flex flex-col items-center gap-1">
            {/* Show existing brand pipeline statuses */}
            {pipelineEntries && pipelineEntries.length > 0 && (
              <div className="flex flex-col gap-1">
                {pipelineEntries.map((entry, idx) => {
                  const colorClass = stageColors[entry.stage] || 'bg-gray-100 text-gray-700';
                  return (
                    <span key={idx} className={`inline-flex items-center text-[9px] font-semibold px-2 py-0.5 rounded-full capitalize ${colorClass}`} title={`${entry.brand_name}: ${entry.stage}`}>
                      {entry.brand_name}: {entry.stage}
                    </span>
                  );
                })}
              </div>
            )}
            {/* Always show Prospect button to add for more brands */}
            <button
              className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 transform hover:scale-105 hover:shadow-lg shadow-green-500/30 text-white text-[10px] font-medium py-0.5 px-2 rounded-lg transition-all duration-300 flex items-center gap-1 mx-auto"
              onClick={(e) => handleProspectClick(e, affiliateUrl, email, row.original.id, row.original.brand_id)}
              disabled={addingToPipeline[affiliateUrl]}
            >
              {addingToPipeline[affiliateUrl] ? (
                <>
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="w-3 h-3" />
                  Prospect
                </>
              )}
            </button>
          </div>
        );
      },
      enableSorting: false,
    }),

  ];

  // Configure table
  const table = useReactTable({
    data,
    columns,
    // Key rows by the affiliate's real id so per-row state (contactResults,
    // socialMediaResults, findingContact) stays bound to the affiliate across
    // pagination/sort/search. Without this, tanstack defaults row.id to the
    // array index, and state leaks between different affiliates that happen
    // to land on the same index after the data changes.
    getRowId: (row, index) => (row?.id != null ? String(row.id) : String(index)),
    state: {
      sorting,
      globalFilter,
      expanded,
      columnVisibility,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onExpandedChange: (updater) => {
      if (singleExpand) {
        // When singleExpand is true, collapse all other rows and expand only the clicked one
        setExpanded((old) => {
          const newExpanded = typeof updater === 'function' ? updater(old) : updater;
          // Find which row is being expanded (not collapsed)
          const expandedKeys = Object.keys(newExpanded).filter(key => newExpanded[key]);
          const oldExpandedKeys = Object.keys(old).filter(key => old[key]);

          if (expandedKeys.length > 1) {
            // Multiple rows in expanded state - find the NEW one (the one that was just clicked)
            // The new key is the one that wasn't in the old state
            const newKey = expandedKeys.find(key => !oldExpandedKeys.includes(key));
            if (newKey) {
              return { [newKey]: true };
            }
            // Fallback: keep the first one
            return { [expandedKeys[0]]: true };
          } else if (expandedKeys.length === 1) {
            // Only one row expanded, keep it (could be toggling same row)
            return { [expandedKeys[0]]: true };
          }
          // No rows expanded (user collapsed the only expanded row)
          return newExpanded;
        });
      } else {
        setExpanded(updater);
      }
    },
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    ...(paginationInfo ? {
      // Server-side pagination
      manualPagination: true,
      manualFiltering: !!onSearch, // Disable client-side filtering when server-side search is enabled
      pageCount: paginationInfo.totalPages,
    } : {
      // Client-side pagination
      getPaginationRowModel: getPaginationRowModel(),
      initialState: {
        pagination: {
          pageSize: defaultPageSize,
        },
      },
    }),
    globalFilterFn: 'includesString',
    getRowCanExpand: () => expandable,
  });

  // Handle pagination
  const handlePageChange = (newPage) => {
    
    // Validate the new page number
    if (newPage < 0 || newPage >= currentPaginationInfo.totalPages) {
      return;
    }
    
    if (paginationInfo && onFetchData) {
      // Server-side pagination - use onFetchData (preferred)
      onFetchData(newPage);
    } else if (paginationInfo && onPageChange) {
      // Fallback to onPageChange for server-side
      onPageChange(newPage);
    } else {
      // Client-side pagination
      table.setPageIndex(newPage);
      // Also update client pagination state
      setClientPaginationState(prev => ({ ...prev, pageIndex: newPage }));
    }
  };

  // FIXED: Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    setRowsPerPage(newPageSize);

    if (paginationInfo && onPageSizeChange) {
      // Server-side pagination - notify parent component
      onPageSizeChange(newPageSize);
    } else if (paginationInfo && onFetchData) {
      // Alternative: Call onFetchData with current page and new page size
      // Note: You might need to modify the parent component to handle page size in onFetchData
      // For now, we'll assume onPageSizeChange is the preferred method
    } else {
      // Client-side pagination
      table.setPageSize(newPageSize);
      // Also update client pagination state and reset to first page
      setClientPaginationState({ pageIndex: 0, pageSize: newPageSize });
    }
  };

  // Generate pagination numbers with proper ellipsis
  const generatePaginationNumbers = () => {
    const currentPage = currentPaginationInfo.page + 1; // Convert to 1-based
    const totalPages = currentPaginationInfo.totalPages;
    const pages = [];

    if (totalPages <= 1) return pages;

    // Only show previous, current, and next page — no jumping allowed
    if (currentPage > 1) pages.push(currentPage - 1);
    pages.push(currentPage);
    if (currentPage < totalPages) pages.push(currentPage + 1);

    return pages;
  };

  if (!data || data.length === 0) {
    return (
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-6 transition-colors duration-300">
        <div className="text-center py-8">
          {loading ? (
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600 dark:text-emerald-400" />
              <div className="text-gray-600 dark:text-slate-300 text-lg font-medium">
                {loadingMessage || 'Loading data...'}
              </div>
            </div>
          ) : (
            <>
              <div className="text-gray-500 dark:text-slate-400 text-lg">No data available</div>
              <div className="text-gray-400 dark:text-slate-500 text-sm mt-2">Try adjusting your filters or search criteria</div>
            </>
          )}
        </div>
      </div>
    );
  }

  const renderHeaderContent = (header) => {
    const getHeaderIcon = (headerId) => {
      switch (headerId) {
        case 'action':
          return null;
        case 'affiliate_url':
          return <Globe className='w-4 h-4'/>;
        case 'total_traffic':
          return <BarChart3 className='w-4 h-4'/>;
        case 'organic_traffic':
          return <TrendingUp className='w-4 h-4'/>;
        case 'paid_traffic':
          return <MousePointer className='w-4 h-4'/>;
        case 'top_geo_organic_traffic':
          return <Flag className='w-4 h-4'/>;
        case 'email':
          return <Mail className='w-4 h-4'/>;
        case 'vertical':
          return <Tag className='w-4 h-4'/>;
        case 'licence_authorities':
          return <ShieldCheck className='w-4 h-4'/>;
        default:
          return <BarChart3 className='w-4 h-4'/>;
      }
    };

    return (
      <button className="flex items-center justify-center gap-2 text-xs font-medium text-blue-600 dark:text-blue-400 tracking-wider hover:text-blue-700 dark:hover:text-blue-300 transition-colors w-full">
        {getHeaderIcon(header.id)}
        {flexRender(header.column.columnDef.header, header.getContext())}
      </button>
    );
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm transition-colors duration-300">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
        <div className="flex-1 max-w-md">
          <h3 className="text-lg font-medium text-gray-800 dark:text-slate-100">{title}</h3>
          <p className="text-sm text-gray-600 dark:text-slate-400">
            {currentPaginationInfo.total ? `${currentPaginationInfo.total} affiliates found` : `${data.length} affiliates found`}
          </p>
        </div>

        <div className='flex items-center space-x-4'>
          {toolbarControls}

          {searchable && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-slate-500 h-4 w-4" />
              <input
                type="text"
                placeholder="Search by URL, keywords..."
                value={onSearch ? searchInputValue : (globalFilter ?? '')}
                onChange={(e) => handleSearchInputChange(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64 text-sm transition-all duration-200 hover:shadow-md focus:shadow-md bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-500"
              />
            </div>
          )}

          {/* Newly Added Affiliate Toggle */}
          {onSortByLatestChange && (
            <div className="flex items-center h-[38px] px-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg">
              <label className="text-sm font-medium text-gray-700 dark:text-slate-300 mr-2 whitespace-nowrap">Newly Added Affiliate</label>
              <button
                type="button"
                onClick={() => {
                  onSortByLatestChange(!sortByLatest);
                }}
                disabled={loading}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed ${
                  sortByLatest ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                    sortByLatest ? 'translate-x-5' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          )}

          {/* FIXED: Page size selector - hidden when hidePageSize is true */}
          {!hidePageSize && (
            <div className="relative">
              <select
                value={rowsPerPage}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                disabled={loading}
                className="border border-gray-200 dark:border-slate-600 rounded-md px-3 py-2 pr-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 transition-all duration-200 hover:shadow-md focus:shadow-md cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
              </select>
            </div>
          )}
          
          {/* Shortlist and Settings buttons - COMMENTED OUT */}
          {/* <div className="relative flex items-center space-x-3">
            <button className='relative flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 border border-gray-200 rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-md'>
              <BookmarkPlus className='text-gray-400 h-4 w-4'/>
              <span className='text-sm font-medium text-gray-700'>Shortlist</span>
              <div className='absolute -top-2 -right-2 bg-blue-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium'>
                1
              </div>
            </button>
            <button className='flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 border border-gray-200 rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-md'>
              <Settings2 className='text-gray-400 h-4 w-4'/>
              <span className='text-sm font-medium text-gray-700'>Settings</span>
            </button>
          </div> */}
        </div>
      </div>

      {/* Table Container with Loading Overlay */}
      <div className="relative">
        {loading && (
          <div className="absolute inset-0 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm flex items-center justify-center z-10 rounded-b-xl">
            <div className="flex flex-col items-center gap-3 px-6 py-4 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-200 dark:border-slate-700">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600 dark:text-emerald-400" />
              <div className="text-center">
                <div className="text-gray-800 dark:text-slate-200 font-semibold text-sm">
                  {loadingMessage || 'Loading...'}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-slate-800 dark:to-slate-800 border-b border-gray-200 dark:border-slate-700">
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th key={header.id} className="px-6 py-4 text-center whitespace-nowrap">
                      {header.isPlaceholder ? null : (
                        renderHeaderContent(header)
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="bg-white dark:bg-slate-900 divide-y divide-gray-200 dark:divide-slate-700">
              {table.getRowModel().rows.map((row, rowIdx) => (
                <React.Fragment key={row.id}>
                  {renderBeforeRow && renderBeforeRow(row.original, rowIdx, table.getRowModel().rows)}
                  {(() => {
                  // Approached rows stand out across the whole row, with a stripe on the left.
                  const rowApproached = shouldShowData(row.index) && row.original.affiliate_url && approachedOf(row.original.affiliate_url).isApproached;
                  return (
                  <tr className={`${!shouldShowData(row.index) ? 'group hover:bg-gray-50 dark:hover:bg-slate-800' : rowApproached ? 'bg-amber-50 hover:bg-amber-100 dark:bg-amber-900/20 dark:hover:bg-amber-900/30' : 'hover:bg-gray-50 dark:hover:bg-slate-800'} transition-all duration-200 relative`}>
                    {shouldShowData(row.index) ? (
                      // Show normal data cells
                      row.getVisibleCells().map((cell, cellIdx) => (
                        <td
                          key={cell.id}
                          className={`px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900 dark:text-slate-200 ${rowApproached && cellIdx === 0 ? 'border-l-4 border-amber-400 dark:border-amber-500' : ''}`}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))
                    ) : (
                      // Show blurred image row with upgrade overlay
                      <td colSpan={row.getVisibleCells().length} className="p-0 relative">
                        {/* Blurred image */}
                        <img 
                          src="./blurrow.png" 
                          alt={`Preview of ${row.original.affiliate_url || 'affiliate'}`}
                          className="w-full object-cover"
                          onError={(e) => { 
                            // Fallback to blurred content if image fails to load
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'block';
                          }}
                        />
                        
                        {/* Fallback blurred content (hidden by default) */}
                        <div className="blur-sm opacity-60 pointer-events-none" style={{ display: 'none' }}>
                          <div className="flex items-center justify-between px-6 py-4">
                            <div className="flex items-center gap-4">
                              <div className="w-6 h-6 bg-gray-200 rounded"></div>
                              <div className="space-y-1">
                                <div className="h-4 bg-gray-200 rounded w-32"></div>
                                <div className="h-3 bg-gray-200 rounded w-24"></div>
                              </div>
                            </div>
                            <div className="flex items-center gap-6">
                              <div className="h-4 bg-gray-200 rounded w-16"></div>
                              <div className="h-4 bg-gray-200 rounded w-16"></div>
                              <div className="h-4 bg-gray-200 rounded w-16"></div>
                              <div className="h-4 bg-gray-200 rounded w-20"></div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Hover Overlay with Upgrade Button */}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-75 transition-all duration-300 flex items-center justify-center">
                          <button
                            onClick={() => {
                              // Navigate to pricing plan page
                              window.location.href = '/pricing-plan';
                            }}
                            className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-90 group-hover:scale-100 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium px-6 py-3 rounded-lg shadow-lg hover:shadow-xl flex items-center gap-2"
                          >
                            <Crown className="w-5 h-5" />
                            <span>Upgrade to View Data</span>
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                  );
                  })()}
                  {/* Expanded Row Content - only for accessible data rows */}
                  {row.getIsExpanded() && shouldShowData(row.index) && (
                    <tr>
                      <td colSpan={row.getVisibleCells().length} className="px-6 py-6 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-slate-800 dark:to-slate-800 border-t dark:border-slate-700">
                        <div className="space-y-6">
                          {/* Custom expanded content or default content */}
                          {renderExpandedRow ? (
                            renderExpandedRow(row.original)
                          ) : (
                            <div className="space-y-6">
                              {/* Per-Vertical Relevant Pages — affiliate recruitment only.
                                  Reads scanned_pages_json populated by the untapped opportunity
                                  validation pipeline. Keys are "{country}:{vertical}".
                                  Renders one section per selected vertical (e.g. Casino + Sports
                                  if both selected). Falls through to the legacy SERP Validated
                                  Pages block below when no per-vertical data exists. */}
                              {(() => {
                                if (!selectedVerticals || selectedVerticals.length === 0) return null;
                                if (!selectedCountries || selectedCountries.length === 0) return null;
                                const scanned = row.original.scanned_pages_json || {};
                                if (!scanned || typeof scanned !== 'object' || Object.keys(scanned).length === 0) return null;

                                // Map UI vertical name → DB vertical key (matches fetchData.js VERTICAL_NAME_MAPPING)
                                const verticalKeyMap = {
                                  'casino': 'casino',
                                  'sports betting': 'sports_betting',
                                  'sports': 'sports_betting',
                                  'poker': 'poker',
                                  'lottery': 'lottery',
                                  'bingo': 'bingo',
                                  'sweepstake': 'sweepstake',
                                  'sweepstakes': 'sweepstake',
                                  'crypto': 'crypto'
                                };

                                // Country ISO code resolution.
                                // selectedCountryCodes (if provided) contains ISO 2-letter codes matching
                                // the keys written by saveScannedPages (e.g. "GB"). If not provided, fall
                                // back to selectedCountries which may be full names like "United Kingdom".
                                // Either way, expand GB/UK aliases so both storage variants are tried.
                                const expandCountryAliases = (code) => {
                                  const upper = (code || '').toUpperCase().trim();
                                  if (upper === 'GB' || upper === 'UK') return ['GB', 'UK'];
                                  return [upper];
                                };
                                // Use ISO codes when available, otherwise use raw selectedCountries values
                                const lookupCountries = (selectedCountryCodes && selectedCountryCodes.length > 0)
                                  ? selectedCountryCodes
                                  : selectedCountries;

                                // For each (country × vertical), look up RELEVANT scanned pages only.
                                // saveScannedPages normalises the vertical key by stripping _ and spaces
                                // (sports_betting → sportsbetting) so we try both forms.
                                // Only pages with isRelevant===true are included.
                                const sections = [];
                                for (const vert of selectedVerticals) {
                                  const dbVert = verticalKeyMap[vert.trim().toLowerCase()] || vert.trim().toLowerCase();
                                  const normVert = dbVert.replace(/[_\s]+/g, ''); // matches saveScannedPages key format
                                  const collectedPages = [];
                                  for (const country of lookupCountries) {
                                    const countryCodes = expandCountryAliases(country);
                                    for (const cc of countryCodes) {
                                      // Try normalised key first (e.g. "GB:casino", "GB:sportsbetting")
                                      // then original dbVert for backward compat ("GB:sports_betting")
                                      const entries =
                                        scanned[`${cc}:${normVert}`] ||
                                        scanned[`${cc}:${dbVert}`] ||
                                        // Legacy format: bare country key (old casino-only data)
                                        (dbVert === 'casino' ? scanned[cc] : null) ||
                                        [];
                                      if (Array.isArray(entries)) {
                                        for (const p of entries) {
                                          if (!p) continue;
                                          const url = typeof p === 'string' ? p : p.url;
                                          if (!url) continue;
                                          // Only show pages the pipeline confirmed as relevant
                                          const isRelevant = typeof p === 'string' ? true : (p.isRelevant === true);
                                          if (isRelevant) {
                                            collectedPages.push({ url, country: cc, confidence: p.confidence || 0 });
                                          }
                                        }
                                      }
                                    }
                                  }

                                  // Dedupe URLs
                                  const seen = new Set();
                                  const uniquePages = collectedPages.filter(p => {
                                    const k = p.url.toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
                                    if (seen.has(k)) return false;
                                    seen.add(k);
                                    return true;
                                  });

                                  if (uniquePages.length > 0) {
                                    sections.push({ vertical: vert, dbVertical: dbVert, pages: uniquePages });
                                  }
                                }

                                if (sections.length === 0) return null;

                                // Color per vertical
                                const verticalColors = {
                                  'casino': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
                                  'sports_betting': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
                                  'poker': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
                                  'lottery': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
                                  'bingo': 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-400',
                                  'sweepstake': 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
                                  'crypto': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                                };

                                // Flatten all sections into a single deduplicated page list
                                // (one section per vertical, but we render as a single list like Top Pages)
                                const allPages = [];
                                const globalSeen = new Set();
                                for (const section of sections) {
                                  for (const page of section.pages) {
                                    const k = page.url.toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
                                    if (!globalSeen.has(k)) {
                                      globalSeen.add(k);
                                      allPages.push({ ...page, vertical: section.vertical });
                                    }
                                  }
                                }

                                // Country display name for the "Relevant for X" tag
                                const countryDisplayName = selectedCountries && selectedCountries[0]
                                  ? selectedCountries[0].replace(/^United Kingdom.*/, 'UK').replace(/^United States.*/, 'US')
                                  : (selectedCountryCodes && selectedCountryCodes[0]) || '';

                                return (
                                  <div className="space-y-3">
                                    <div className="flex items-center gap-2">
                                      <Globe className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                      <h4 className="font-semibold text-gray-800 dark:text-slate-100">Relevant Pages</h4>
                                    </div>
                                    <div className="bg-white dark:bg-slate-900 rounded-lg p-3 shadow-sm dark:border dark:border-slate-700">
                                      <div className="space-y-2 max-h-48 overflow-y-auto">
                                        {allPages.map((page, idx) => (
                                          <div key={idx} className="py-2 border-b border-gray-100 dark:border-slate-700 last:border-b-0">
                                            <div className="flex items-center gap-2 mb-1">
                                              <span className="text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 px-2 py-1 rounded flex-shrink-0">{idx + 1}</span>
                                              <a
                                                href={page.url.startsWith('http') ? page.url : `https://${page.url}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-gray-800 dark:text-slate-200 hover:text-blue-600 dark:hover:text-blue-400 hover:underline text-xs break-all block transition-colors"
                                              >
                                                {page.url}
                                              </a>
                                            </div>
                                            {countryDisplayName && (
                                              <div className="pl-8">
                                                <span className="text-[10px] text-green-700 dark:text-green-400 font-medium">
                                                  Relevant for {countryDisplayName}
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}

                              {/* SERP Validated Pages - Show multiple URLs on separate lines */}
                              {/* Shown ONLY when the Relevant Pages block above rendered nothing
                                  (i.e. scanned_pages_json has no isRelevant===true entry for the
                                  selected country+vertical). Falls through to this block for affiliates
                                  that were validated via a fast path (TLD classification, blocked news,
                                  etc.) where no scanned pages were saved. When no filters are active,
                                  this block always shows as before. */}
                              {(() => {
                                // Suppress SERP block when the Relevant Pages block above already
                                // rendered isRelevant===true pages (avoid showing both).
                                // Falls through when: no filters set, scanned_pages_json is empty,
                                // or no relevant pages found (affiliate has no casino pages).
                                if (selectedVerticals && selectedVerticals.length > 0 &&
                                    selectedCountries && selectedCountries.length > 0) {
                                  const scanned = row.original.scanned_pages_json || {};
                                  if (scanned && typeof scanned === 'object' && Object.keys(scanned).length > 0) {
                                    const vkMap = {
                                      'casino': 'casino', 'sports betting': 'sports_betting', 'sports': 'sports_betting',
                                      'poker': 'poker', 'lottery': 'lottery', 'bingo': 'bingo',
                                      'sweepstake': 'sweepstake', 'sweepstakes': 'sweepstake', 'crypto': 'crypto'
                                    };
                                    const expA = (c) => { const u = (c || '').toUpperCase().trim(); return (u === 'GB' || u === 'UK') ? ['GB', 'UK'] : [u]; };
                                    const lkCodes = (selectedCountryCodes && selectedCountryCodes.length > 0) ? selectedCountryCodes : selectedCountries;
                                    for (const vert of selectedVerticals) {
                                      const dv = vkMap[vert.trim().toLowerCase()] || vert.trim().toLowerCase();
                                      const nv = dv.replace(/[_\s]+/g, '');
                                      for (const country of lkCodes) {
                                        for (const cc of expA(country)) {
                                          const entries = scanned[`${cc}:${nv}`] || scanned[`${cc}:${dv}`] || (dv === 'casino' ? scanned[cc] : null) || [];
                                          if (Array.isArray(entries) && entries.some(p => p && (typeof p === 'string' || p.isRelevant === true))) {
                                            return null; // Relevant Pages block rendered → hide SERP block
                                          }
                                        }
                                      }
                                    }
                                  }
                                }

                                if (!row.original.serp_validated_page && !row.original.affiliate_url_confidence) return null;

                                // Parse and deduplicate URLs from both fields
                                const splitUrls = (str) => {
                                  if (!str) return [];
                                  const matches = str.match(/https?:\/\/[^\s,]+/g);
                                  if (!matches) return [];
                                  return matches.map(u => u.replace(/[,;]+$/, '').trim()).filter(u => u.length > 10);
                                };
                                let urls = splitUrls(row.original.affiliate_url_confidence);
                                for (const u of splitUrls(row.original.serp_validated_page)) {
                                  const norm = u.toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
                                  if (!urls.some(x => x.toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '') === norm)) {
                                    urls.unshift(u);
                                  }
                                }
                                const seen = new Set();
                                const uniqueUrls = urls.filter(u => {
                                  const k = u.toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
                                  if (seen.has(k)) return false;
                                  seen.add(k);
                                  return true;
                                });

                                if (uniqueUrls.length === 0) return null;

                                const countryLabel = selectedCountries && selectedCountries[0]
                                  ? selectedCountries[0].replace(/^United Kingdom.*/, 'UK').replace(/^United States.*/, 'US')
                                  : '';

                                return (
                                  <div className="space-y-3">
                                    <div className="flex items-center gap-2">
                                      <Globe className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                      <h4 className="font-semibold text-gray-800 dark:text-slate-100">Relevant Pages</h4>
                                    </div>
                                    <div className="bg-white dark:bg-slate-900 rounded-lg p-3 shadow-sm dark:border dark:border-slate-700">
                                      <div className="space-y-2 max-h-48 overflow-y-auto">
                                        {uniqueUrls.map((url, idx) => (
                                          <div key={idx} className="py-2 border-b border-gray-100 dark:border-slate-700 last:border-b-0">
                                            <div className="flex items-center gap-2 mb-1">
                                              <span className="text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 px-2 py-1 rounded flex-shrink-0">{idx + 1}</span>
                                              <a
                                                href={url.startsWith('http') ? url : `https://${url}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-gray-800 dark:text-slate-200 hover:text-blue-600 dark:hover:text-blue-400 hover:underline text-xs break-all block transition-colors"
                                              >
                                                {url}
                                              </a>
                                            </div>
                                            {countryLabel && (
                                              <div className="pl-8">
                                                <span className="text-[10px] text-green-700 dark:text-green-400 font-medium">
                                                  Relevant for {countryLabel}
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}

                              {/* Pipeline / Prospected Brands */}
                              {(() => {
                                const entries = pipelineStatuses[row.original.affiliate_url] || [];
                                if (entries.length === 0) return null;
                                const stageColorMap = {
                                  prospected: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800',
                                  contacted: 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800',
                                  negotiating: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800',
                                  onboarding: 'bg-cyan-100 text-cyan-700 border-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-400 dark:border-cyan-800',
                                  active: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800',
                                  churned: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800'
                                };
                                return (
                                  <div className="space-y-3">
                                    <div className="flex items-center gap-2">
                                      <Target className="w-4 h-4 text-green-600 dark:text-green-400" />
                                      <h4 className="font-semibold text-gray-800 dark:text-slate-100">Prospected Brands</h4>
                                    </div>
                                    <div className="bg-white dark:bg-slate-900 rounded-lg p-4 shadow-sm dark:border dark:border-slate-700">
                                      <div className="flex flex-wrap gap-2">
                                        {entries.map((entry, idx) => (
                                          <div key={idx} className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${stageColorMap[entry.stage] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                                            <span className="text-sm font-semibold">{entry.brand_name}</span>
                                            <span className="text-[10px] uppercase font-bold opacity-75">{entry.stage}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}

                              {/* Live Affiliate Brand Page - Where the brand is listed */}
                              {row.original.live_affiliate_brand_url && Object.keys(row.original.live_affiliate_brand_url).length > 0 && (
                                <div className="space-y-3">
                                  <div className="flex items-center gap-2">
                                    <ExternalLink className="w-4 h-4 text-green-600 dark:text-green-400" />
                                    <h4 className="font-semibold text-gray-800 dark:text-slate-100">Brand Listed On</h4>
                                  </div>
                                  <div className="bg-white dark:bg-slate-900 rounded-lg p-4 shadow-sm dark:border dark:border-slate-700">
                                    <div className="space-y-2">
                                      {Object.entries(row.original.live_affiliate_brand_url).map(([brand, pageUrl], idx) => (
                                        <div key={idx} className="flex items-center gap-3 py-1">
                                          <span className="text-xs font-medium text-gray-600 dark:text-slate-400 min-w-[120px]">{brand}</span>
                                          <a
                                            href={pageUrl.startsWith('http') ? pageUrl : `https://${pageUrl}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline transition-colors flex items-center gap-2 text-sm"
                                          >
                                            <span className="break-all">{pageUrl}</span>
                                            <ExternalLink className="w-3 h-3 flex-shrink-0" />
                                          </a>
                                        </div>
                                      ))}
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-slate-400 mt-3 pt-2 border-t dark:border-slate-700">
                                      Verified pages where the brand is actively listed or promoted.
                                    </p>
                                  </div>
                                </div>
                              )}

                              {/* Country Breakdown - Full Width */}
                       <div className="space-y-3">
  <div className="flex items-center gap-2">
    <Flag className="w-4 h-4 text-blue-600 dark:text-blue-400" />
    <h4 className="font-semibold text-gray-800 dark:text-slate-100">Country Breakdown</h4>
  </div>
  <div className="bg-white dark:bg-slate-900 rounded-lg p-4 shadow-sm dark:border dark:border-slate-700">
    {row.original.top_geo_organic_traffic ? (
      <div className="grid grid-cols-4 gap-4">
        {getProcessedGeos(row.original.top_geo_organic_traffic).map((geo, index) => (
          <div key={index} className="flex justify-between items-center py-3 px-4 bg-gray-50 dark:bg-slate-800 rounded-lg border dark:border-slate-700">
            <div className="flex items-center gap-3">
              <CountryFlag countryCode={geo.country} className="w-6 h-4" />
              <span className="font-medium text-gray-800 dark:text-slate-200">{geo.country}</span>
            </div>
            <span className="text-blue-600 dark:text-blue-400 font-semibold">{formatNumber(geo.count)}</span>
          </div>
        ))}
      </div>
    ) : (
      <div className="text-gray-500 dark:text-slate-400 text-center py-4">No country data available</div>
    )}
  </div>
</div>
                              {/* Two Column Layout for Pages and Keywords */}
                              <div className="grid grid-cols-2 gap-6">
                                {/* Top Pages */}
                                <div className="space-y-3">
                                  <div className="flex items-center gap-2">
                                    <Globe className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                    <h4 className="font-semibold text-gray-800 dark:text-slate-100">Top Pages</h4>
                                  </div>
                                  <div className="bg-white dark:bg-slate-900 rounded-lg p-3 shadow-sm dark:border dark:border-slate-700">
                                    {row.original.top_page ? (
                                      <div className="space-y-2 max-h-48 overflow-y-auto">
                                        {(() => {
                                          // Split, trim, filter empty values, and remove duplicates
                                          const pages = [...new Set(
                                            row.original.top_page
                                              .split(',')
                                              .map(p => p.trim())
                                              .filter(p => p && p.length > 0)
                                          )].slice(0, 10);

                                          return pages.map((page, index) => (
                                            <div key={index} className="py-2 border-b border-gray-100 dark:border-slate-700 last:border-b-0">
                                              <div className="flex items-center gap-2 mb-2">
                                                <span className="text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 px-2 py-1 rounded">{index + 1}</span>
                                                <a
                                                  href={formatUrl(page)}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="text-gray-800 dark:text-slate-200 hover:text-blue-600 dark:hover:text-blue-400 hover:underline text-xs break-all block pl-6 transition-colors"
                                                >
                                                  {page}
                                                </a>
                                              </div>
                                            </div>
                                          ));
                                        })()}
                                      </div>
                                    ) : (
                                      <div className="text-gray-500 dark:text-slate-400 text-center py-4">No top pages available</div>
                                    )}
                                  </div>
                                </div>

                                {/* Top Organic Keywords */}
                                <div className="space-y-3">
                                  <div className="flex items-center gap-2">
                                    <TrendingUp className="w-4 h-4 text-green-600 dark:text-green-400" />
                                    <h4 className="font-semibold text-gray-800 dark:text-slate-100">Top Organic Keywords</h4>
                                  </div>
                                  <div className="bg-white dark:bg-slate-900 rounded-lg p-3 shadow-sm dark:border dark:border-slate-700">
                                    {row.original.keywords ? (
                                      <div className="space-y-2 max-h-48 overflow-y-auto">
                                        {getUniqueKeywords(row.original.keywords).slice(0, 10).map((keyword, index) => (
                                          <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-slate-700 last:border-b-0">
                                            <div className="flex items-center gap-2">
                                              <span className="text-xs bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 px-2 py-1 rounded">{index + 1}</span>
                                              <span className="text-gray-700 dark:text-slate-300 text-xs">{keyword}</span>
                                            </div>
                                            <span className="text-green-600 dark:text-green-400 text-xs font-medium">Organic</span>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <div className="text-gray-500 dark:text-slate-400 text-center py-4">No organic keywords available</div>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Paid Keywords - Full Width */}
                              <div className="space-y-3">
                                <div className="flex items-center gap-2">
                                  <MousePointer className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                                  <h4 className="font-semibold text-gray-800 dark:text-slate-100">Top Paid Keywords</h4>
                                </div>
                                <div className="bg-white dark:bg-slate-900 rounded-lg p-3 shadow-sm dark:border dark:border-slate-700">
                                  {(() => {
                                    // Live keywords, then the ones they used to
                                    // bid on. Taking an affiliate off paid
                                    // traffic strips its keywords from
                                    // paid_keyword, so a domain that clearly ran
                                    // "best online casino" last month showed
                                    // "No paid keywords available" here while the
                                    // Paid Advertising panel right below listed
                                    // it. The log still has it, so show it —
                                    // marked past, never mixed in as current.
                                    const current = getUniqueKeywords(row.original.paid_keyword);
                                    const seen = new Set(current.map((k) => k.toLowerCase()));

                                    const past = (row.original.paid_activity?.evidence || [])
                                      .filter((e) => (e.channel || 'google') === 'google' && e.label)
                                      .map((e) => e.label)
                                      .filter((k) => {
                                        const key = k.toLowerCase();
                                        if (seen.has(key)) return false;
                                        seen.add(key);
                                        return true;
                                      });

                                    const keywords = [
                                      ...current.slice(0, 12).map((k) => ({ keyword: k, past: false })),
                                      ...past.slice(0, 6).map((k) => ({ keyword: k, past: true }))
                                    ];

                                    if (keywords.length === 0) {
                                      return <div className="text-gray-500 dark:text-slate-400 text-center py-4">No paid keywords available</div>;
                                    }

                                    return (
                                      <div className="grid grid-cols-3 gap-3">
                                        {keywords.map(({ keyword, past: isPast }, index) => (
                                          <div
                                            key={index}
                                            className={`flex justify-between items-center py-2 px-3 rounded-lg border ${
                                              isPast
                                                ? 'bg-gray-50 dark:bg-slate-800/60 border-gray-200 dark:border-slate-700'
                                                : 'bg-purple-50 dark:bg-purple-900/30 border-purple-100 dark:border-purple-800'
                                            }`}
                                          >
                                            <div className="flex items-center gap-2 min-w-0">
                                              <span
                                                className={`text-xs px-2 py-1 rounded flex-shrink-0 ${
                                                  isPast
                                                    ? 'bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400'
                                                    : 'bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300'
                                                }`}
                                              >
                                                {index + 1}
                                              </span>
                                              <span className="text-gray-700 dark:text-slate-300 text-xs truncate" title={keyword}>{keyword}</span>
                                            </div>
                                            <span
                                              className={`text-xs font-medium flex-shrink-0 ml-1 ${
                                                isPast ? 'text-gray-400 dark:text-slate-500' : 'text-purple-600 dark:text-purple-400'
                                              }`}
                                            >
                                              {isPast ? 'Past' : 'Paid'}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    );
                                  })()}
                                </div>
                              </div>

                              {/* Paid advertising record — where they advertise
                                  now and, when the ads have stopped, what they
                                  ran and when. This is where the detail that
                                  used to crowd the URL cell lives. */}
                              <PaidAdvertisingPanel row={row.original} />

                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* FIXED: Move Tooltip outside of pagination conditional - Always render tooltip */}
      <Tooltip 
        data={tooltipData} 
        position={tooltipPosition} 
        formatNumber={formatNumber} 
      />

      {/* Pagination */}
      {currentPaginationInfo.totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200 dark:border-slate-700">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700 dark:text-slate-300">
              Showing {currentPaginationInfo.page * currentPaginationInfo.pageSize + 1} to{' '}
              {Math.min((currentPaginationInfo.page + 1) * currentPaginationInfo.pageSize, currentPaginationInfo.total)} of{' '}
              {currentPaginationInfo.total} results
            </div>

            <div className="flex items-center gap-1">
              {/* Previous button */}
              <button
                onClick={() => handlePageChange(currentPaginationInfo.page - 1)}
                disabled={!currentPaginationInfo.hasPreviousPage || loading}
                className="p-2 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 disabled:opacity-50 disabled:cursor-not-allowed rounded-md hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {/* Page numbers */}
              {generatePaginationNumbers().map((pageNum, index) => {
                if (pageNum === '...') {
                  return (
                    <span key={`ellipsis-${index}`} className="px-3 py-2 text-gray-500 dark:text-slate-400">
                      ...
                    </span>
                  );
                }

                const isCurrentPage = pageNum === currentPaginationInfo.page + 1;

                return (
                  <button
                    key={pageNum}
                    onClick={() => {
                      const targetPage = pageNum - 1; // Convert to 0-based
                      handlePageChange(targetPage);
                    }}
                    disabled={loading}
                    className={`px-3 py-2 text-sm rounded-md transition-colors ${
                      isCurrentPage
                        ? 'bg-blue-600 text-white font-medium'
                        : 'text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-slate-100'
                    } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {pageNum}
                  </button>
                );
              })}

              {/* Next button */}
              <button
                onClick={() => handlePageChange(currentPaginationInfo.page + 1)}
                disabled={!currentPaginationInfo.hasNextPage || loading}
                className="p-2 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 disabled:opacity-50 disabled:cursor-not-allowed rounded-md hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Brand Selection Popup for Pipeline - Centered Modal */}
      {brandPopup.visible && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center" onClick={() => { setBrandPopup(prev => ({ ...prev, visible: false })); setConfirmingRemove(null); }}>
          <div className="absolute inset-0 bg-black/30" />
          <div
            id="brand-pipeline-popup"
            className="relative bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-gray-200 dark:border-slate-600 p-5 w-full max-w-sm mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">Select Brand for Prospecting</p>
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5 truncate max-w-[250px]">{brandPopup.affiliateUrl}</p>
              </div>
              <button onClick={() => { setBrandPopup(prev => ({ ...prev, visible: false })); setConfirmingRemove(null); }} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-1.5 max-h-[280px] overflow-y-auto">
              {userBrands.length === 0 ? (
                <p className="text-sm text-gray-400 py-6 text-center">No brands found. Add a brand first.</p>
              ) : (
                userBrands.map(brand => {
                  const existingEntries = pipelineStatuses[brandPopup.affiliateUrl] || [];
                  const pipelineEntry = existingEntries.find(e => e.brand_id === brand.id);
                  const alreadyAdded = !!pipelineEntry;
                  const isConfirming = confirmingRemove === pipelineEntry?.id;

                  // Show inline confirmation for this brand
                  if (alreadyAdded && isConfirming) {
                    return (
                      <div key={brand.id} className="px-4 py-3 text-sm rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
                        <p className="text-red-700 dark:text-red-300 text-xs mb-2">Remove <strong>{brand.brand_name || brand.brand_url}</strong>?</p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleRemoveFromPipeline(pipelineEntry.id, brandPopup.affiliateUrl)}
                            className="flex-1 py-1.5 text-xs font-medium text-white bg-red-500 hover:bg-red-600 rounded-md transition-colors"
                          >
                            Yes, Remove
                          </button>
                          <button
                            onClick={() => setConfirmingRemove(null)}
                            className="flex-1 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-md transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={brand.id}
                      className={`w-full text-left px-4 py-3 text-sm rounded-lg transition-all flex items-center justify-between border ${
                        alreadyAdded
                          ? 'bg-gray-50 dark:bg-slate-700/50 text-gray-400 dark:text-slate-500 border-gray-100 dark:border-slate-700'
                          : 'hover:bg-green-50 dark:hover:bg-green-900/20 text-gray-700 dark:text-gray-200 border-gray-100 dark:border-slate-700 hover:border-green-300 dark:hover:border-green-800 hover:shadow-sm cursor-pointer'
                      }`}
                      onClick={() => !alreadyAdded && handleAddToPipeline(brandPopup.affiliateUrl, brandPopup.email, brand.id)}
                    >
                      <span className="truncate font-medium">{brand.brand_name || brand.brand_url}</span>
                      {alreadyAdded ? (
                        <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                          <span className="text-[10px] bg-gray-100 dark:bg-slate-600 text-gray-500 dark:text-slate-400 px-2 py-0.5 rounded-full whitespace-nowrap">Already added</span>
                          <button
                            onClick={(e) => { e.stopPropagation(); setConfirmingRemove(pipelineEntry.id); }}
                            className="p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
                            title="Remove from pipeline"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <Plus className="w-4 h-4 text-green-500 flex-shrink-0 ml-2" />
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* Already approached: pick the brand the site was approached for. A
          ticked brand is taken back with another click. */}
      {approachPopup.visible && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center" onClick={() => setApproachPopup({ visible: false, affiliateUrl: '' })}>
          <div className="absolute inset-0 bg-black/30" />
          <div
            className="relative bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-gray-200 dark:border-slate-600 p-5 w-full max-w-sm mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">Already approached for which brand?</p>
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5 truncate max-w-[250px]">{approachPopup.affiliateUrl}</p>
              </div>
              <button onClick={() => setApproachPopup({ visible: false, affiliateUrl: '' })} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-1.5 max-h-[280px] overflow-y-auto">
              {userBrands.length === 0 ? (
                <p className="text-sm text-gray-400 py-6 text-center">No brands found. Add a brand first.</p>
              ) : (
                userBrands.map(brand => {
                  const marked = (approachedStatus[approachPopup.affiliateUrl]?.brands || []).some(b => b.brand_id === brand.id);
                  const saving = savingApproach === brand.id;
                  return (
                    <button
                      key={brand.id}
                      type="button"
                      disabled={savingApproach !== null}
                      onClick={() => handleToggleApproached(approachPopup.affiliateUrl, brand)}
                      className={`w-full text-left px-4 py-3 text-sm rounded-lg transition-all flex items-center justify-between border disabled:cursor-wait ${
                        marked
                          ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 border-amber-200 dark:border-amber-800'
                          : 'hover:bg-amber-50 dark:hover:bg-amber-900/20 text-gray-700 dark:text-gray-200 border-gray-100 dark:border-slate-700 hover:border-amber-300 dark:hover:border-amber-800'
                      }`}
                      title={marked ? 'Click to take the mark back' : 'Mark as approached for this brand'}
                    >
                      <span className="truncate font-medium">{brand.brand_name || brand.brand_url}</span>
                      {saving ? (
                        <Loader2 className="w-4 h-4 animate-spin text-amber-500 flex-shrink-0 ml-2" />
                      ) : marked ? (
                        <span className="flex items-center gap-1 text-[10px] font-semibold ml-2 flex-shrink-0">
                          <Check className="w-3.5 h-3.5" /> Approached
                        </span>
                      ) : (
                        <Plus className="w-4 h-4 text-amber-500 flex-shrink-0 ml-2" />
                      )}
                    </button>
                  );
                })
              )}
            </div>
            {approachError && <p className="text-xs text-red-600 dark:text-red-400 mt-3">{approachError}</p>}
            <p className="text-[11px] text-gray-500 dark:text-slate-400 mt-3">
              With the "Already Approached" filter on No, approached sites are hidden from your results.
            </p>
          </div>
        </div>
      )}

      {/* Every stored channel for one affiliate. Picking an email from here
          hands off to the EmailPopup below, so the composer is unchanged. */}
      <ContactPopup
        isOpen={contactPopup.visible}
        onClose={() => setContactPopup(prev => ({ ...prev, visible: false }))}
        domain={contactPopup.domain}
        contacts={contactPopup.contacts}
        scan={contactPopup.scan}
        authors={contactPopup.authors}
        onSelectEmail={(contact) => {
          // Offer every stored address in the composer's To field, with the one
          // the user clicked first, so a mis-click is one dropdown away from
          // being fixed rather than a reopen.
          const emails = (contactPopup.contacts || [])
            .filter(c => c.channel === 'email')
            .map(c => c.value);
          const ordered = [contact.value, ...emails.filter(v => v !== contact.value)];
          setSelectedEmailData(ordered.join(', '));
          setSelectedSocialMedia({});
          setContactPopup(prev => ({ ...prev, visible: false }));
          setEmailPopupVisible(true);
        }}
      />

      {/* Forex Deal column details — see DealDetailsPopup above. */}
      <DealDetailsPopup
        isOpen={dealPopup.visible}
        onClose={() => setDealPopup((prev) => ({ ...prev, visible: false }))}
        row={dealPopup.row}
      />

      {/* Email Popup */}
      <EmailPopup
        isOpen={emailPopupVisible}
        onClose={() => { setEmailPopupVisible(false); setSelectedSocialMedia({}); }}
        recipient={{ email: selectedEmailData?.split(',')[0]?.trim() || '', value: selectedEmailData?.split(',')[0]?.trim() || '' }}
        allEmails={selectedEmailData ? selectedEmailData.split(',').map(e => e.trim()).filter(Boolean) : []}
        socialMedia={selectedSocialMedia}
        onSend={async (emailForm, recipient) => {
          const apiUrl = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';
          const response = await fetch(`${apiUrl}/api/emailsent`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ to: emailForm.to, cc: emailForm.cc || '', subject: emailForm.subject, message: emailForm.message, attachments: emailForm.attachments || [], sender_email_id: emailForm.sender_email_id || 'primary' })
          });
          if (!response.ok) {
            const errText = await response.text().catch(() => '');
            throw new Error(errText || `Failed to send email (${response.status})`);
          }
          const result = await response.json().catch(() => ({}));
          if (!result.success) throw new Error(result.error || 'Failed to send email');
        }}
      />
    </div>
  );
};

export default UniversalTable;